import React, { useState, useEffect, useRef, useMemo } from "react";
import ConfirmHeader from "../../components/ConfirmHeader";
import { intervalLabel } from "../../utils/habitInterval";
import { createPortal as createPortalDom } from "react-dom";
import {
  AlertCircle, Archive, ArrowLeft, BarChart2, Check, CheckSquare, ChevronLeft, ChevronRight,
  Edit3, GripVertical, Heart, Layers, Milestone, PartyPopper, Pause, PauseCircle, Pencil, Play, Plus,
  RotateCcw, Save, Settings, SkipForward, SlidersHorizontal, Sprout, Trash2, Undo2, X,
} from "lucide-react";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { MinusCircle } from "../../PhosphorIcons";
import Modal from "../../components/Modal";
import { PanelWarmup } from "../../components/PanelWarmup";
import { S, CONFIRM_STACK, CONFIRM_DANGER, CONFIRM_CANCEL, CONFIRM_PRIMARY, SKIP_CAL_SHADOW } from "../../theme/sharedStyles";
import { isDarkBgTheme, themeTone, vividPastel, readableOnWhite } from "../../theme/colorEngine";
import { MONTHS, DAYS_SHORT } from "../../constants/dates";
import { CategoryIcon } from "../../components/CategoryIcon";
import { NativePickerInput } from "../../components/NativePickerInput";
import { cleanHabitForm, canToggleHabitReminder, setHabitReminderOn } from "../../utils/habitFormSave";
import { withPendingGoal, goalFormFlags, goalOn } from "../../utils/habitWeeklyGoal";
import { isAmountHabit, loggedAmount, dailyGoalOf, formatProgress, formatDailyGoal, goalTag } from "../../utils/habitUnits";
import { ShieldChip } from "../../components/ShieldChip";
import { cleanDays, chipTone } from "../../utils/restrictedChip";
import { AddAmountSheet, AmountButton } from "../../components/AddAmountSheet";
import { HabitIcon } from "../../components/HabitIcon";
import { AttachmentField } from "../../components/attachments/AttachmentField";
import { AttachmentViewer } from "../../components/attachments/AttachmentViewer";
import { PullTabDrawer } from "../../components/PullTabDrawer";
import { HabitCheckbox, MultiCountButton } from "../../components/HabitCheckbox";
import { CategoriesTab } from "../categories/CategoriesTab";
import { ProfileTab } from "../profile/ProfileTab";
import { renderHabitForm } from "./renderHabitForm";
import { stopAlarmSound } from "../../utils/alarmSound";
import { load, save, uid } from "../../utils/storage";
import { now, fmt, todayStr, localDateStr } from "../../utils/dateHelpers";
import {
  limitedOffDays, isLimitedRestrictedOn, habitYearlyDueDate, isHabitCountedYearly, isHabitHiddenFromDaily,
  isHabitPaused, isHabitDueToday, isWithinHabitRange, getConsistencyScore,
  allowancePeriodBounds, allowanceUsed, hasExcludableDates,
} from "../../utils/habitLogic";
import { ExcludedDatesValue } from "../../components/ExcludedDatesValue";
import { markHabitTicked } from "../../onboarding/onboardingStorage";
import { vibrantAccent } from "../../capacitor/widgetColor";
import { useSheetDrag } from "../../utils/sheetDrag";

// Habits stays mounted (display:none) while another tab shows, so its body
// portals are hidden by one CSS rule in App.jsx (html[data-tab]) rather than
// by re-rendering. display:contents keeps the wrapper box-less.
const createPortal = (node, target) => createPortalDom(<div data-habits-portal="" style={{ display: "contents" }}>{node}</div>, target);

// Wraps one habit row so it can be drag-reordered in the "Custom" habits
// view. When `enabled` is false it's an inert pass-through div — same DOM
// shape either way, so it's safe to use unconditionally around every row in
// every view mode without touching that row's own markup/logic.
function SortableHabitRow({ id, enabled, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled: !enabled });
  if (!enabled) return <div>{children}</div>;
  return (
    <div ref={setNodeRef} style={{
      position: "relative",
      transform: CSS.Transform.toString(transform), transition,
      zIndex: isDragging ? 10 : "auto",
      boxShadow: isDragging ? "0 8px 24px rgba(0,0,0,0.18)" : "none",
    }}>
      {/* Overlaid inside the row's own box (not a separate sibling strip) so it
          picks up the exact same background/blur as the row — a sibling strip
          showed a faint seam of the page gradient bleeding through on glass
          themes, since it sat outside the row's own tinted/blurred surface. */}
      <span {...attributes} {...listeners}
        style={{ position: "absolute", left: 4, top: "50%", transform: "translateY(-50%)", zIndex: 2, display: "flex", cursor: "grab", touchAction: "none", color: "var(--c-text-soft)" }}>
        <GripVertical size={15} />
      </span>
      {children}
    </div>
  );
}

export function HabitsTab({ habits, setHabits, setDeletedHabits = () => {}, habitLogs, setHabitLogs, categories, setCategories = () => {}, habitSkips, setHabitSkips, tasks, setTasks, calEvents = [], setCalEvents = () => {}, cleanLog = {}, setCleanLog = () => {}, glassMode = false, masculineMode = false, isDesktop = false, pendingHabitEditId = null, clearPendingHabitEdit = () => {}, onOpenSettings = () => {}, savedColors = [], addSavedColor = () => {}, settingsOpen = false, primaryColor, bgTheme }) {
  const [showForm, setShowForm] = useState(false);
  const [showStats, setShowStats] = useState(false); // habit analytics sub-page
  // Inline "+ New category" expansion inside the habit form's category picker
  // (add + edit modals never show at the same time, so one flag covers both).
  const [showNewCatInForm, setShowNewCatInForm] = useState(false);
  const emptyForm = { name: "", emoji: "⭐", phosphorIcon: "", emojiLocked: false, goalId: "", frequencyType: "daily", days: [], dayLabels: "named", monthDates: [], weeklyCount: 3, monthlyCount: 10, monthlyMode: "dates", yearlyMode: "dates", yearDates: [], yearlyCount: 12, intervalEvery: 2, intervalUnit: "day", color: "#f4a7b9", dailyCount: 1, unit: "count", unitLabel: "", habitType: "todo", limitedOffDays: [], limitedInterval: false, maxTimesPerDay: 1, quitReason: "", quitDate: todayStr(), habitStartDate: "", endDate: "", excludedDates: [], restrictedTimeStart: "22:00", restrictedTimeEnd: "06:00", restrictedDays: [], restrictedMonthDates: [], allowanceMode: "", allowanceMax: 2, showOnDailyList: undefined, description: "", addToClockIn: true, timeOfDay: [] };
  const [form, setForm] = useState({ ...emptyForm, habitType: "todo" });
  const [editingHabit, setEditingHabit] = useState(null);
  const [editForm, setEditForm] = useState(null);
  // Which Save button (if any) is mid pop-then-close animation — see renderHabitForm's `saving` prop.
  const [savePopping, setSavePopping] = useState(null); // null | "add" | "edit"
  const triggerSavePop = (which, commit) => {
    if (savePopping) return;
    setSavePopping(which);
    setTimeout(() => { commit(); setSavePopping(null); }, 380);
  };
  const [relapseModal, setRelapseModal] = useState(null); // { id, context } or habitId string
  const [relapseNote, setRelapseNote] = useState("");
  const [expandedHabit, setExpandedHabit] = useState(null); // habit id for expanded action panel
  const [habitDetailModal, setHabitDetailModal] = useState(null); // habit object for popup detail panel
  const [confirmSlip, setConfirmSlip] = useState(null); // habit id pending slip confirmation
  const [confirmDeleteHabit, setConfirmDeleteHabit] = useState(null); // habit object pending delete confirmation
  const [manageModal, setManageModal] = useState(false); // Manage ⚙ bottom sheet
  // Both bottom sheets follow a downward swipe and slide away; a tap on the dim
  // slides them down too (useSheetDrag, Jade 2026-10-22).
  const manageSheet = useSheetDrag(() => setManageModal(false));
  const pauseSheet = useSheetDrag(() => { setPauseModal(null); setPauseUntilDate(""); setPauseReason(""); });
  // Habit cards view: grouped by category (default), grouped by time of day,
  // or a freely drag-reordered custom order. customHabitOrder only matters in
  // "custom" mode — it's a list of habit ids; anything not in it (new habits)
  // sorts to the end.
  const [habitViewMode, setHabitViewMode] = useState(() => load("clarity_habitViewMode", "category"));
  const [customHabitOrder, setCustomHabitOrder] = useState(() => load("clarity_habitsCustomOrder", []));
  useEffect(() => { save("clarity_habitViewMode", habitViewMode); }, [habitViewMode]);
  useEffect(() => { save("clarity_habitsCustomOrder", customHabitOrder); }, [customHabitOrder]);
  // Stop any alarm-sound preview whenever the add/edit panel closes (X, Cancel,
  // Save, or unmount) — renderHabitForm is a plain render function (called
  // conditionally, so it can't own its own cleanup effect), so this lives here
  // at the tab level instead, covering every close path in one place.
  useEffect(() => {
    if (!showForm && !editForm) stopAlarmSound();
    return () => stopAlarmSound();
  }, [showForm, editForm]);
  const habitDndSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));
  const [viewMenuOpen, setViewMenuOpen] = useState(false); // "arrange by" popover, anchored to its icon button
  const viewMenuRef = useRef(null);
  useEffect(() => {
    if (!viewMenuOpen) return;
    const onDocClick = (e) => { if (viewMenuRef.current && !viewMenuRef.current.contains(e.target)) setViewMenuOpen(false); };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [viewMenuOpen]);
  const [addMenuOpen, setAddMenuOpen] = useState(false); // unified "+" menu (desktop): new habit / new category
  const openAddCategoryRef = React.useRef(null); // set by the embedded CategoriesTab to open its add form
  const catColRef = React.useRef(null); // the scrollable categories column, so we can scroll its add-form into view
  const [expandedManageHabit, setExpandedManageHabit] = useState(null); // habit id expanded in manage modal
  const [hiddenDrawerOpen, setHiddenDrawerOpen] = useState(false); // "Long-term habits" drawer on the daily list
  // Counted habit (weekly/monthly "X times") completion. Once the period target
  // is fully hit, a ✓-dismiss button appears next to the checkbox. Tapping it
  // (after a confirm) slides the row out and clears it from the daily list for
  // the rest of THIS period only — the dismissal is stamped with the period's
  // start date and persisted on the habit (so it survives tab switches / reloads
  // / other devices), and the card naturally returns when the next period's
  // window begins (a fresh target). Not auto-dismissed — the user chooses.
  const [exitingCountHabits, setExitingCountHabits] = useState({});
  const [confirmDismissCount, setConfirmDismissCount] = useState(null); // habit pending dismiss confirmation
  // A counted ("N times a week/month/year") day counts once anything is
  // logged — except for a unit habit (habitUnits.js), whose day counts only
  // once its amount reaches the day's goal (500 of 10,000 steps isn't a day).
  const dayCountsTowardPeriod = (h, v, key) => (isAmountHabit(h)
    ? loggedAmount(h, v) >= dailyGoalOf(goalOn(h, key))
    : !!v);
  // The Add amount sheet for unit habits: { habitId, date } or null.
  const [amountSheet, setAmountSheet] = useState(null);
  const getCountPeriodInfo = (h) => {
    const isYearlyCounted = isHabitCountedYearly(h);
    const isPeriodCounted = h.frequencyType === "count" || (h.frequencyType === "monthly" && h.monthlyMode === "count") || isYearlyCounted;
    if (!isPeriodCounted) return null;
    const isMonthlyCounted = h.frequencyType === "monthly" && h.monthlyMode === "count";
    const periodLen = isYearlyCounted ? 365 : isMonthlyCounted ? 30 : 7;
    const habitOrigin = h.habitStartDate || localDateStr(h.createdAt) || t;
    const originMs = new Date(habitOrigin + "T00:00:00").getTime();
    const todayMs = new Date(t + "T00:00:00").getTime();
    const periodIdx = Math.max(0, Math.floor((todayMs - originMs) / (periodLen * 86400000)));
    const periodStartMs = originMs + periodIdx * periodLen * 86400000;
    const periodStartStr = fmt(new Date(periodStartMs));
    const target = isYearlyCounted ? (h.yearlyCount || 1) : isMonthlyCounted ? (h.monthlyCount || 1) : (h.weeklyCount || 1);
    let done = 0;
    for (let i = 0; i < periodLen; i++) {
      const key = fmt(new Date(periodStartMs + i * 86400000));
      if (key > t) break;
      if (dayCountsTowardPeriod(h, habitLogs[key]?.[h.id], key)) done++;
    }
    const periodEnd = new Date(periodStartMs + (periodLen - 1) * 86400000);
    // Compact card label: "due today" once the period's deadline IS today,
    // instead of spelling out today's own date — the full "due today, Sep 27"
    // form belongs to the detail panel (see countedDetail below), not this
    // space-constrained inline badge.
    const isDueToday = fmt(periodEnd) === t;
    const dueLabel = isDueToday ? "today" : `${MONTHS[periodEnd.getMonth()].slice(0, 3)} ${periodEnd.getDate()}`;
    return { periodStartMs, periodStartStr, target, done, hit: done >= target, periodEnd, dueLabel, isDueToday };
  };
  // True once the user has dismissed this counted habit for the current period.
  const isCountDismissedThisPeriod = (h) => {
    const info = getCountPeriodInfo(h);
    if (!info) return false;
    return (h.countDismissedPeriods || []).includes(info.periodStartStr);
  };
  // Slide the row out (task-style), then persist the dismissal for this period.
  const dismissCountHabit = (h) => {
    const info = getCountPeriodInfo(h);
    setConfirmDismissCount(null);
    if (!info) return;
    setExitingCountHabits(p => ({ ...p, [h.id]: true }));
    setTimeout(() => {
      setHabits(prev => prev.map(x => x.id === h.id
        ? { ...x, countDismissedPeriods: [...(x.countDismissedPeriods || []).filter(d => d !== info.periodStartStr), info.periodStartStr].slice(-6) }
        : x));
      setExitingCountHabits(p => { const n = { ...p }; delete n[h.id]; return n; });
    }, 380);
  };
  const [pauseModal, setPauseModal] = useState(null); // habit object for pause flow
  const [pauseUntilDate, setPauseUntilDate] = useState(""); // date string (only used in "date" mode)
  const [pauseMode, setPauseMode] = useState("date"); // "date" = pause until a date, "forever" = until resumed
  const [pauseCustom, setPauseCustom] = useState(false); // "Pick a date" chip chosen
  const [pauseReason, setPauseReason] = useState(""); // optional reason text

  // ── Skip / "Can't do this today" flow ──
  const [skipFlow, setSkipFlow] = useState(null); // { habitId, habitName, habitEmoji, step: "confirm", rescheduledTo }
  const [skipCalMonth, setSkipCalMonth] = useState(() => { const d = new Date(); return { y: d.getFullYear(), m: d.getMonth() }; });

  const t = todayStr();

  const logHabit = (habitId, dateStr, val) => {
    // Single choke point for every checkbox tick / counter increment / instance
    // log across this whole tab — the one place to note "the user has actually
    // logged a completion at least once", which gates the Habit Stats scroll
    // coach marks (ScrollCoachMark.jsx via ProfileTab.jsx).
    if (val) markHabitTicked();
    setHabitLogs(prev => ({ ...prev, [dateStr]: { ...(prev[dateStr] || {}), [habitId]: val } }));
  };

  // ── Allowance / "budget" mode for restricted habits ───────────────────────
  // limited habits can be tracked as "up to N times per
  // week/month" instead of per-day. Attempts are stored in habit.attemptLog
  // (date-stamped); going over the cap = over budget (a slip).
  // The day this habit's allowance refills — the START of the next period,
  // not the last day of the current one. Deliberately different from a to-do
  // counted habit's "due <date>": there the period end is a deadline to finish
  // by, here it's the moment the budget resets to full. Periods are anchored
  // to the habit's own start date (see allowancePeriodBounds).
  const dateLabel = (dateStr) => {
    const d = new Date(dateStr + "T00:00:00");
    return `${MONTHS[d.getMonth()].slice(0, 3)} ${d.getDate()}`;
  };
  const allowanceResetLabel = (h) => dateLabel(allowancePeriodBounds(h, t).reset);
  const countAttempts = (h) => (h.allowanceMode ? allowanceUsed(h, t) : 0);
  const addAttempt = (id) => setHabits(p => p.map(h => h.id === id ? { ...h, attemptLog: [...(h.attemptLog || []), t] } : h));
  const removeAttempt = (id) => setHabits(p => p.map(h => {
    if (h.id !== id) return h;
    const log = [...(h.attemptLog || [])];
    const { start } = allowancePeriodBounds(h, t);
    // drop the most recent entry within the current period
    for (let i = log.length - 1; i >= 0; i--) { if (log[i] >= start && log[i] <= t) { log.splice(i, 1); break; } }
    return { ...h, attemptLog: log };
  }));

  // Compact daily card for budget-mode (allowance) restricted habits.
  const renderBudgetCard = (h, cat, _i) => {
    const used = countAttempts(h);
    const max = h.allowanceMax || 1;
    const usedToday = (h.attemptLog || []).some(d => d === todayStr());
    const over = used > max;
    const atLimit = used >= max;
    const period = (h.allowanceMode === "month" || h.allowanceMode === "monthDates") ? "month" : "week";
    const tone = over ? { c: "#A32D2D", bg: "#FCEBEB", bd: "#F09595" }
      : atLimit ? { c: "#854F0B", bg: "#FEF8E7", bd: "#F5C842" }
      : { c: "#3B6D11", bg: "#F0FAF0", bd: "#AFD89A" };
    return (
      <div key={h.id} className="clarity-row-item" data-tour={h.id === firstHabitCardId ? "first-habit-card" : undefined} style={{
        borderTop: _i === 0 ? "none" : `1px solid ${cat ? (cat.color + "33") : "#f0d8ea"}`,
        background: "transparent", padding: "10px 12px", animationDelay: `${_i * 0.04}s`,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div onClick={() => setHabitDetailModal(h)} style={{ cursor: "pointer", flexShrink: 0 }}>
            <HabitIcon h={h} size={18} color={cat?.color} />
          </div>
          <div onClick={() => setHabitDetailModal(h)} style={{ flex: 1, minWidth: 0, cursor: "pointer", userSelect: "none" }}>
            <span style={{ fontSize: 14, color: "var(--c-text)", fontWeight: 700 }}>{h.name}</span>
            <div style={{ display: "flex", alignItems: "center", marginTop: 3, flexWrap: "wrap" }}>
              <span style={{ fontSize: 10, fontWeight: 700, color: tone.c }}>
                {over ? `🌧️ over by ${used - max}` : `☀️ ${max - used} left this ${period}`}
              </span>
              {/* When the count goes back to zero. The to-do counted card's
                  equivalent line reads "due <date>" — a deadline to finish by;
                  this one is the opposite, so it says "resets" instead. */}
              <span style={{ fontSize: 10, color: "var(--c-text-soft)", fontStyle: "italic", whiteSpace: "nowrap", marginLeft: 5 }}>
                · resets {allowanceResetLabel(h)}
              </span>
            </div>
            {h.endDate && (() => { const dl = Math.ceil((new Date(h.endDate + "T00:00:00") - new Date(todayStr() + "T00:00:00")) / 86400000); return <div style={{ fontSize: 10, color: "#a040c8", marginTop: 1, fontWeight: 600 }}>{dl === 0 ? "ends today 🏁" : dl === 1 ? "ends tomorrow" : `ends ${h.endDate}`}</div>; })()}
          </div>
          {/* used / max counter */}
          <div onClick={e => e.stopPropagation()} style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
            <span onClick={() => setHabitDetailModal(h)} style={{ cursor: "pointer", fontSize: 13, fontWeight: 800, color: tone.c, background: tone.bg, border: `1.5px solid ${tone.bd}`, borderRadius: 99, padding: "3px 10px" }}>{used} / {max}</span>
            {used > 0 && (
              <button onClick={() => removeAttempt(h.id)} title="Undo last attempt"
                style={{ width: 28, height: 28, borderRadius: "50%", border: "1.5px solid #e0d0e0", background: "var(--c-bg-card)", color: "var(--c-text-soft)", cursor: "pointer", fontSize: 16, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", padding: 0 }}>−</button>
            )}
            <button onClick={() => { if (!usedToday) addAttempt(h.id); }} disabled={usedToday} title={usedToday ? "Already used today. Available again tomorrow" : "Confirm attempt"}
              style={{ width: 28, height: 28, borderRadius: "50%", border: `1.5px solid ${usedToday ? "#d0c8d0" : tone.bd}`, background: usedToday ? "var(--c-bg-soft)" : tone.bg, color: usedToday ? "#c0b0c0" : tone.c, cursor: usedToday ? "not-allowed" : "pointer", fontSize: 16, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", padding: 0, opacity: usedToday ? 0.45 : 1 }}>+</button>
          </div>
        </div>
      </div>
    );
  };

  const addHabit = () => {
    if (!form.name.trim()) return;
    const catColor = categories.find(c => c.id === form.goalId)?.color || "#d4789a";
    const newH = { id: uid(), ...cleanHabitForm(form), color: catColor, createdAt: todayStr() };
    if (newH.frequencyType === "yearly" && (newH.yearlyMode || "count") === "count") newH.yearlyCount = Math.max(1, parseInt(newH.yearlyCount, 10) || 1);
    if (newH.habitType === "nottodo") { newH.relapseLog = []; newH.bestStreak = 0; }
    if (newH.habitType === "limited") { newH.instanceLog = {}; newH.relapseLog = []; }
    if (newH.allowanceMode && newH.habitType === "limited") newH.attemptLog = [];
    else newH.allowanceMode = "";
    setHabits(p => [...p, newH]);
    setForm({ ...emptyForm, habitType: "todo" });
    setShowForm(false);
  };

  const openEdit = (h) => {
    setEditingHabit(h.id);
    // frequencyType/timeOfDay are deliberately passed through as-is (not
    // defaulted here) — a habit added via onboarding's starter-habits step
    // leaves them unset (undefined) on purpose, and the form needs to see
    // that undefined to show its "Not set" badge. Everywhere else in the form
    // that reads them already guards with `|| []` / `=== <value>`, so leaving
    // them undefined here doesn't break anything.
    { const g = withPendingGoal(h); setEditForm({ ...goalFormFlags(h), name: h.name, emoji: h.emoji || "⭐", phosphorIcon: h.phosphorIcon || "", goalId: h.goalId || "", frequencyType: h.frequencyType, days: g.days || [], dayLabels: h.dayLabels || "named", monthDates: g.monthDates || [], weeklyCount: g.weeklyCount || 3, monthlyCount: g.monthlyCount || 10, monthlyMode: h.monthlyMode || "dates", yearlyMode: h.yearlyMode || "count", yearDates: g.yearDates || [], yearlyCount: g.yearlyCount ?? 12, intervalEvery: h.intervalEvery ?? 2, intervalUnit: h.intervalUnit || "day", intervalStart: h.intervalStart || "", color: h.color || "#f4a7b9", dailyCount: g.dailyCount || 1, unit: g.unit || "count", unitLabel: g.unitLabel || "", habitType: h.habitType || "todo", limitedOffDays: limitedOffDays(h), limitedInterval: !!h.limitedInterval, maxTimesPerDay: h.maxTimesPerDay || 1, quitReason: h.quitReason || "", quitDate: h.quitDate || todayStr(), habitStartDate: h.habitStartDate || "", endDate: h.endDate || "", excludedDates: Array.isArray(h.excludedDates) ? h.excludedDates : [], restrictedTimeStart: h.restrictedTimeStart || "22:00", restrictedTimeEnd: h.restrictedTimeEnd || "06:00", restrictedDays: h.restrictedDays || [], restrictedMonthDates: h.restrictedMonthDates || [], allowanceMode: h.allowanceMode || "", allowanceMax: h.allowanceMax || 2, showOnDailyList: h.showOnDailyList, description: h.description || "", addToClockIn: h.addToClockIn !== false, timeOfDay: h.timeOfDay, reminderTime: h.reminderTime || "", reminderType: h.reminderType || "notification", reminderOffsets: Array.isArray(h.reminderOffsets) && h.reminderOffsets.length ? h.reminderOffsets : [0], alarmSound: h.alarmSound || "", alarmSoundUri: h.alarmSoundUri || "", alarmSoundLabel: h.alarmSoundLabel || "" }); }
  };

  const saveEdit = () => {
    if (!editForm.name.trim()) return;
    const catColor = categories.find(c => c.id === editForm.goalId)?.color || "#d4789a";
    const budget = editForm.allowanceMode && editForm.habitType === "limited";
    const ef = cleanHabitForm(editForm, habits.find(h => h.id === editingHabit));
    if (ef.frequencyType === "yearly" && (ef.yearlyMode || "count") === "count") ef.yearlyCount = Math.max(1, parseInt(ef.yearlyCount, 10) || 1);
    setHabits(p => p.map(h => h.id === editingHabit ? { ...h, ...ef, allowanceMode: budget ? ef.allowanceMode : "", attemptLog: h.attemptLog || [], allowedDays: undefined, color: catColor, ...(ef.addToClockIn ? { clockInRestoreDate: undefined } : {}) } : h));
    setEditingHabit(null); setEditForm(null);
  };
  const cancelEdit = () => { setEditingHabit(null); setEditForm(null); setShowNewCatInForm(false); };

  // Open the editor for a habit requested from the Clock-In queue (after tab-switch)
  useEffect(() => {
    if (!pendingHabitEditId) return;
    const h = habits.find(x => x.id === pendingHabitEditId);
    if (h) openEdit(h);
    clearPendingHabitEdit();
  }, [pendingHabitEditId]); // eslint-disable-line

  // ── NOT-TO-DO helpers ──
  const getCleanStreak = (h) => {
    let streak = 0;
    const d = new Date();
    for (let i = 0; i < 365; i++) {
      const key = fmt(d);
      const slipped = h.relapseLog?.some(r => r.date === key);
      if (slipped) break;
      if (i > 0 || key <= t) streak++;
      d.setDate(d.getDate() - 1);
    }
    return streak;
  };

  const logRelapse = (h, slipContext) => {
    const note = relapseNote.trim();
    const newLog = { date: t, note, ...(slipContext || {}) };
    const streak = getCleanStreak(h);
    setHabits(p => p.map(x => x.id === h.id ? { ...x, relapseLog: [...(x.relapseLog || []), newLog], wobbleLog: (x.wobbleLog || []).filter(w => w.date !== t), bestStreak: Math.max(x.bestStreak || 0, streak) } : x));
    setRelapseModal(null); setRelapseNote("");
  };

  // Log a "wobble" — tempted but held the line. Counts as a clean day; never breaks a run.
  const logWobble = (h) => {
    setHabits(p => p.map(x => x.id === h.id ? {
      ...x,
      wobbleLog: (x.wobbleLog || []).some(w => w.date === t) ? (x.wobbleLog || []) : [...(x.wobbleLog || []), { date: t }],
      relapseLog: (x.relapseLog || []).filter(r => r.date !== t), // a wobble overrides any slip logged today
    } : x));
  };
  const clearTodayMark = (h) => {
    setHabits(p => p.map(x => x.id === h.id ? {
      ...x,
      wobbleLog: (x.wobbleLog || []).filter(w => w.date !== t),
      relapseLog: (x.relapseLog || []).filter(r => r.date !== t),
    } : x));
  };

  // ── Skip helpers ──
  const openSkipFlow = (h) => {
    const d = new Date();
    setSkipCalMonth({ y: d.getFullYear(), m: d.getMonth() });
    setSkipFlow({ habitId: h.id, habitName: h.name, habitEmoji: h.emoji || "⭐", step: "confirm", rescheduledTo: null });
  };

  const saveSkip = () => {
    if (!skipFlow) return;
    const entry = {
      id: uid(),
      type: "todo",
      habitId: skipFlow.habitId,
      habitName: skipFlow.habitName,
      habitEmoji: skipFlow.habitEmoji,
      skippedDate: t,
      rescheduledTo: skipFlow.rescheduledTo || null,
      loggedAt: new Date().toISOString(),
    };
    setHabitSkips(p => [...p, entry]);
    // If rescheduled, mark the original log as skipped so streak isn't broken
    // (we don't mark it done — user still needs to do it on the rescheduled day).
    // Also record the move on the habit itself (isHabitDueOn reads it) so the
    // habit actually becomes due on the picked day instead of `rescheduledTo`
    // being pure stats bookkeeping — see habitLogic.js.
    if (skipFlow.rescheduledTo) {
      setHabits(p => p.map(h => h.id === skipFlow.habitId ? {
        ...h,
        movedOccurrences: [...(h.movedOccurrences || []), { from: t, to: skipFlow.rescheduledTo }],
      } : h));
    }
    setSkipFlow(null);
  };

  const cancelSkip = () => { setSkipFlow(null); };

  // ── PAUSE helpers ──
  const pauseDateIn = (days) => { const d = new Date(); d.setDate(d.getDate() + days); return fmt(d); };
  const openPauseModal = (h) => {
    setPauseUntilDate(pauseDateIn(7)); // smart default: 1 week
    setPauseMode("date");
    setPauseCustom(false);
    setPauseReason(""); // reset reason
    setPauseModal(h);
  };

  const confirmPause = () => {
    if (!pauseModal) return;
    if (pauseMode === "date" && !pauseUntilDate) return; // needs a date first
    const until = pauseMode === "forever" ? "9999-12-31" : pauseUntilDate;
    const entry = { pausedOn: todayStr(), pausedUntil: until, reason: pauseReason.trim() };
    setHabits(p => p.map(h => {
      if (h.id !== pauseModal.id) return h;
      // Migrate legacy pauseReason string into pauseHistory if needed
      const existingHistory = h.pauseHistory || (h.pauseReason ? [{ pausedOn: h.createdAt || todayStr(), pausedUntil: h.pausedUntil || until, reason: h.pauseReason }] : []);
      return { ...h, pausedUntil: until, pauseReason: undefined, pauseHistory: [...existingHistory, entry] };
    }));
    setPauseModal(null);
    setPauseUntilDate("");
    setPauseReason("");
  };

  const resumeHabit = (habitId) => {
    setHabits(p => p.map(h => h.id === habitId ? { ...h, pausedUntil: "" } : h));
  };

  // ── LIMITED helpers ──
  const getTodayInstances = (h) => {
    const val = habitLogs[t]?.[h.id];
    return typeof val === "number" ? val : (val ? 1 : 0);
  };

  const isAllowedToday = (h) => {
    // A limited habit is "allowed" on days that are NOT in its off (restricted)
    // set. Blank off set = restricted every day → never allowed.
    return !isLimitedRestrictedOn(h, todayStr());
  };

  const getComplianceRate = (h) => {
    let clean = 0, total = 0;
    const d = new Date();
    for (let i = 0; i < 30; i++) {
      const key = fmt(d);
      if (key < (h.createdAt || t)) break;
      const restricted = isLimitedRestrictedOn(h, key);
      if (restricted) { total++; const v = habitLogs[key]?.[h.id]; if (!v || v === 0) clean++; }
      d.setDate(d.getDate() - 1);
    }
    return total === 0 ? 100 : Math.round((clean / total) * 100);
  };

  const todoHabits = habits.filter(h => (h.habitType || "todo") === "todo");
  // A boundary card is shown when its restriction is "active" today:
  //  • nottodo (full stop) → restricted every day → always visible
  //  • limited            → only visible on days it's actually restricted (i.e. NOT
  //                         one of its allowed days) — on allowed days there's no
  //                         boundary to show, so the card drops off the daily list.
  // Start/end date + pause guard is shared with the To Do list (isWithinHabitRange)
  // so restricted habits behave identically across their start/end date window.
  const restrictedHabits = habits.filter(h => (h.habitType === "limited" || h.habitType === "nottodo") && isWithinHabitRange(h, t) && !isHabitHiddenFromDaily(h) && !(h.habitType === "limited" && isAllowedToday(h)));
  const todayHabits = todoHabits.filter(h => isHabitDueToday(h) && !isHabitHiddenFromDaily(h));
  // Habits hidden from the daily list (e.g. yearly-counted) live in a drawer.
  const hiddenHabits = habits.filter(h => isHabitHiddenFromDaily(h) && isWithinHabitRange(h, t));
  // First habit card actually shown on the dashboard today — used to anchor
  // the coach-mark tour's "tap this card" step to a real, existing card.
  const firstHabitCardId = todayHabits[0]?.id ?? restrictedHabits[0]?.id;


  // ── Habit analytics sub-page (tracker / target / bar graph + history) ──
  if (showStats) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <button onClick={() => setShowStats(false)} className="hb-head-btn"
          style={{ display: "flex", alignItems: "center", gap: 6, alignSelf: "flex-start", padding: "8px 14px", borderRadius: 99, border: "1.5px solid var(--c-border-mid)", background: "var(--c-bg-card)", color: "var(--c-text-mid)", fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", boxShadow: "0 2px 8px rgba(180,100,140,0.08)" }}>
          <ChevronLeft size={15} /> Back to Habits
        </button>
        <ProfileTab section="habits" isDesktop={isDesktop}
          habits={habits} setHabits={setHabits}
          habitLogs={habitLogs} setHabitLogs={setHabitLogs}
          habitSkips={habitSkips} setHabitSkips={setHabitSkips}
          tasks={tasks} setTasks={setTasks} categories={categories} bgTheme={bgTheme} />
      </div>
    );
  }

  return (
    <div data-tour="habits-area" className="habits-dashboard" style={{ display: "flex", flexWrap: "wrap", gap: 36, alignItems: "flex-start" }}>
      {/* ── Categories column — scrollable spaces beside habits (desktop only) ── */}
      {isDesktop && (
      <div className="habits-cat-col" ref={catColRef}>
        <CategoriesTab categories={categories} setCategories={setCategories} habits={habits} setHabits={setHabits} tasks={tasks} setTasks={setTasks} habitLogs={habitLogs} setHabitLogs={setHabitLogs} calEvents={calEvents} setCalEvents={setCalEvents} habitSkips={habitSkips} setHabitSkips={setHabitSkips} glassMode={glassMode} masculineMode={masculineMode} openAddRef={openAddCategoryRef} isDesktop={isDesktop} savedColors={savedColors} addSavedColor={addSavedColor} primaryColor={primaryColor} />
      </div>
      )}
      <div className="habits-main-col" style={{ display: "flex", flexDirection: "column", gap: 12, flex: "1 1 360px", minWidth: 300 }}>


      {/* Lay the add form out once, invisibly, after load — see PanelWarmup. */}
      {!showForm && !editingHabit && (
        <PanelWarmup id="habit-form">
          <div style={{ background: "var(--c-bg-active)", borderRadius: 18, padding: 24, width: "100%", maxWidth: isDesktop ? 580 : 420, maxHeight: "90vh", overflowY: "auto" }}>
            {renderHabitForm({ ...emptyForm, habitType: "todo" }, () => {}, () => {}, () => {}, "Save", categories)}
          </div>
        </PanelWarmup>
      )}

      {/* Add Form Modal. No pop-in (Jade, 2026-10-09): it just appears on "+", like the edit panel. */}
      {showForm && (
        <Modal onClose={() => { setShowForm(false); setShowNewCatInForm(false); }} blur={false} dim={0.6} animate={false}>
          <div style={{ background: "var(--c-bg-active)", borderRadius: 18, padding: 24, width: "100%", maxWidth: isDesktop ? 580 : 420, maxHeight: "90vh", overflowY: "auto", boxShadow: "0 8px 32px rgba(180,80,120,0.22)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "var(--c-text)" }}>
                New habit
              </h3>
              <button onClick={() => { setShowForm(false); setShowNewCatInForm(false); }} style={{ background: "transparent", border: "none", fontSize: 20, cursor: "pointer", color: "var(--c-text-soft)" }}><X size={16} /></button>
            </div>
            {renderHabitForm(form, setForm, () => triggerSavePop("add", addHabit), () => { setShowForm(false); setShowNewCatInForm(false); }, "Save", categories, { show: showNewCatInForm, setShow: setShowNewCatInForm, setCategories, savedColors, addSavedColor }, savePopping === "add")}
          </div>
        </Modal>
      )}

      {/* Edit Modal */}
      {editingHabit && editForm && (
        <Modal onClose={cancelEdit} blur={false} dim={0.6} animate={false}>
          <div style={{ background: "var(--c-bg-active)", borderRadius: 18, padding: 24, width: "100%", maxWidth: isDesktop ? 580 : 420, maxHeight: "90vh", overflowY: "auto", boxShadow: "0 8px 32px rgba(180,80,120,0.18)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "var(--c-text)" }}><Edit3 size={16} style={{marginRight:6}} />Edit Habit</h3>
              <button onClick={cancelEdit} style={{ background: "transparent", border: "none", fontSize: 20, cursor: "pointer", color: "var(--c-text-soft)" }}><X size={16} /></button>
            </div>
            {renderHabitForm(editForm, setEditForm, () => triggerSavePop("edit", saveEdit), cancelEdit, "Save changes", categories, { show: showNewCatInForm, setShow: setShowNewCatInForm, setCategories, savedColors, addSavedColor }, savePopping === "edit")}
          </div>
        </Modal>
      )}

      {/* Confirm Delete Habit Modal — zIndex above the Manage Habits sheet
          (9100). The delete button there deliberately leaves the sheet open,
          so at Modal's default 9000 this confirmation rendered BEHIND it and
          only became visible after closing the sheet. Blurred/dimmed too, so
          the sheet reads as inert behind the confirmation. */}
      {confirmDeleteHabit && (
        <Modal onClose={() => setConfirmDeleteHabit(null)} dim={0.55} zIndex={9200} animate={false}>
          <div style={{ background: "var(--c-bg-card)", borderRadius: 20, padding: "28px 24px", width: "100%", maxWidth: 340, boxShadow: "0 8px 40px rgba(180,100,140,0.22)", textAlign: "center" }}>
            <ConfirmHeader title="Are you sure you want to delete this habit?" />
            <div style={CONFIRM_STACK}>
              <button onClick={() => setConfirmDeleteHabit(null)} style={CONFIRM_CANCEL}>
                Cancel
              </button>
              <button onClick={() => {
                const delId = confirmDeleteHabit.id;
                setHabits(p => p.filter(x => x.id !== delId));
                setDeletedHabits(prev => ({ ...prev, [delId]: Date.now() }));
                // Purge the deleted habit's logged history everywhere it's stored,
                // so no remnant of its progress lingers in any stat/graph or sync blob.
                setHabitLogs(prev => {
                  const next = {};
                  for (const [date, entries] of Object.entries(prev)) {
                    if (!entries || !(delId in entries)) { next[date] = entries; continue; }
                    const { [delId]: _omit, ...rest } = entries;
                    next[date] = rest;
                  }
                  return next;
                });
                setHabitSkips(p => p.filter(s => s.habitId !== delId));
                setCleanLog(prev => {
                  const next = {};
                  for (const [date, ids] of Object.entries(prev)) next[date] = (ids || []).filter(id => id !== delId);
                  return next;
                });
                setConfirmDeleteHabit(null);
              }}
                style={CONFIRM_DANGER}>
                Delete
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Clear-completed-counted-habit confirmation */}
      {confirmDismissCount && (() => {
        const info = getCountPeriodInfo(confirmDismissCount);
        const isYearlyDismiss = isHabitCountedYearly(confirmDismissCount);
        const period = isYearlyDismiss ? "year" : (confirmDismissCount.frequencyType === "monthly" && confirmDismissCount.monthlyMode === "count") ? "month" : "week";
        return (
          <Modal onClose={() => setConfirmDismissCount(null)} blur={false} dim={0.6}>
            <div style={{ background: "var(--c-bg-card)", borderRadius: 20, padding: "28px 24px", width: "100%", maxWidth: 340, boxShadow: "0 8px 40px rgba(180,100,140,0.22)", textAlign: "center" }}>
              <ConfirmHeader tone="accent" icon={PartyPopper} title={isYearlyDismiss ? "Clear this habit?" : "Clear from today's list?"}
                subtitle={`You've hit your target for ${confirmDismissCount.name}${info ? ` (${info.done}/${info.target})` : ""}. It comes back next ${period}.`} />
              <div style={CONFIRM_STACK}>
                <button onClick={() => setConfirmDismissCount(null)} style={CONFIRM_CANCEL}>Keep it</button>
                <button onClick={() => dismissCountHabit(confirmDismissCount)} style={CONFIRM_PRIMARY}>Clear</button>
              </div>
            </div>
          </Modal>
        );
      })()}

      {/* Relapse Modal */}
      {relapseModal && (() => {
        const relapseId = typeof relapseModal === "object" ? relapseModal.id : relapseModal;
        const relapseContext = typeof relapseModal === "object" ? relapseModal.context : null;
        const slipHabit = habits.find(x => x.id === relapseId);
        return (
          <Modal onClose={() => { setRelapseModal(null); setRelapseNote(""); }} blur={false} dim={0.6}>
            <div style={{ background: "var(--c-bg-card)", borderRadius: 20, padding: "28px 24px", width: "100%", maxWidth: 340, boxShadow: "0 8px 32px rgba(180,80,120,0.18)", textAlign: "center" }}>
              <ConfirmHeader tone="accent" icon={AlertCircle} title={`Log an attempt for "${slipHabit?.name || "this habit"}"?`}
                subtitle={relapseContext === "not-allowed" ? "Used on a restricted day." : relapseContext === "over-limit" ? "Went over the daily limit." : "It's okay. Log it and move on."} />
              <div style={CONFIRM_STACK}>
                <button onClick={() => { setRelapseModal(null); setRelapseNote(""); }} style={CONFIRM_CANCEL}>Cancel</button>
                <button onClick={() => { if (slipHabit) logRelapse(slipHabit, relapseContext); }} style={CONFIRM_PRIMARY}>Log it</button>
              </div>
            </div>
          </Modal>
        );
      })()}

      {/* ── Habit Detail Popup Panel ── */}
      {amountSheet && (() => {
        const sh = habits.find(x => x.id === amountSheet.habitId);
        if (!sh) return null;
        const d = amountSheet.date;
        const shCat = categories.find(c => c.id === sh.goalId);
        return (
          <AddAmountSheet habit={goalOn(sh, d)} rawLog={habitLogs[d]?.[sh.id]} color={shCat?.color || sh.color}
            dateLabel={d === t ? "Today" : new Date(d + "T00:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}
            onLog={(v) => logHabit(sh.id, d, v)} onClose={() => setAmountSheet(null)} />
        );
      })()}
      {habitDetailModal && (() => {
        const h = habitDetailModal;
        const cat = categories.find(c => c.id === h.goalId);
        const isSkippedHDM = habitSkips?.some(s => s.habitId === h.id && s.skippedDate === t);
        const accentColor = cat?.color || h.color || "#d4789a";
        const close = () => setHabitDetailModal(null);
        const DAYS_SHORT = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
        const freqLabel = h.habitType === "limited" && h.limitedInterval && !h.allowanceMode ? `Not allowed ${intervalLabel(h).toLowerCase()}`
          : h.frequencyType === "daily" || !h.frequencyType ? "Every day"
          : h.frequencyType === "interval" ? intervalLabel(h)
          : h.frequencyType === "count" ? `${h.weeklyCount || 1}× per week`
          : h.frequencyType === "specific" ? ((h.days || []).map(i => DAYS_SHORT[i]).join(", ") || "Specific days")
          : h.frequencyType === "monthly" ? (h.monthlyMode === "count" ? `${h.monthlyCount || 10}× per month` : "Monthly")
          : "Every day";
        const typeLabel = h.habitType === "nottodo" ? "Not-to-do" : h.habitType === "limited" ? "Limited" : "To-do";
        // Target completion detail — weekly, monthly & yearly counted habits all
        // show "<done>/<target> done this <period> | due <date>" here instead of
        // as a permanent sub-label on the card (the card now just shows a
        // compact "<done>/<target>" beside the checkbox).
        const countedDetail = (() => {
          const isWeeklyCount = h.frequencyType === "count";
          const isMonthlyCount = h.frequencyType === "monthly" && h.monthlyMode === "count";
          const isYearlyCount = h.frequencyType === "yearly" && (h.yearlyMode || "count") === "count";
          if (!isWeeklyCount && !isMonthlyCount && !isYearlyCount) return null;
          const habitOrigin = h.habitStartDate || localDateStr(h.createdAt) || t;
          const originMs = new Date(habitOrigin + "T00:00:00").getTime();
          const todayMs = new Date(t + "T00:00:00").getTime();
          const periodLen = isYearlyCount ? 365 : isMonthlyCount ? 30 : 7;
          const target = isYearlyCount ? Math.max(1, parseInt(h.yearlyCount, 10) || 1) : isMonthlyCount ? (h.monthlyCount || 1) : (h.weeklyCount || 1);
          const periodLabel = isYearlyCount ? "this year" : isMonthlyCount ? "this month" : "this week";
          const periodIdx = Math.max(0, Math.floor((todayMs - originMs) / (periodLen * 86400000)));
          const periodStartMs = originMs + periodIdx * periodLen * 86400000;
          const periodEnd = new Date(periodStartMs + (periodLen - 1) * 86400000);
          let doneCount = 0;
          for (let i = 0; i < periodLen; i++) {
            const key = fmt(new Date(periodStartMs + i * 86400000));
            if (key > t) break;
            if (habitLogs[key]?.[h.id]) doneCount++;
          }
          const isHitTarget = doneCount >= target;
          // Detail panel spells out both — "due today, Sep 27" — unlike the
          // card's compact "due today" (see getCountPeriodInfo above), since
          // there's room here and the calendar date is still useful detail.
          const calendarLabel = `${MONTHS[periodEnd.getMonth()].slice(0,3)} ${periodEnd.getDate()}`;
          const dueLabel = fmt(periodEnd) === t ? `today, ${calendarLabel}` : calendarLabel;
          return `${doneCount}/${target} done ${periodLabel} | ${isHitTarget ? "✓ done!" : `due ${dueLabel}`}`;
        })();
        // Limited habits on an allowance budget get the same kind of
        // at-a-glance line the counted to-do habits get from countedDetail
        // above — used-vs-cap for this period, plus when it refills.
        const limitDetail = (() => {
          if (h.habitType !== "limited" || !h.allowanceMode) return null;
          const max = h.allowanceMax || 1;
          const used = countAttempts(h);
          const periodLabel = (h.allowanceMode === "month" || h.allowanceMode === "monthDates") ? "this month" : "this week";
          const over = used > max;
          const { start, end } = allowancePeriodBounds(h, t);
          return `${used}/${max} used ${periodLabel} (${dateLabel(start)} – ${dateLabel(end)}) | ${over ? `over by ${used - max} · ` : ""}resets ${allowanceResetLabel(h)}`;
        })();
        const HDMRow = ({ label, value }) => value ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 2, padding: "8px 0", borderBottom: "1px solid #f5eef8" }}>
            <span style={{ fontSize: 9, fontWeight: 800, color: "var(--c-text-soft)", textTransform: "uppercase", letterSpacing: 0.8 }}>{label}</span>
            <span style={{ fontSize: 13, color: "var(--c-text)", lineHeight: 1.5 }}>{value}</span>
          </div>
        ) : null;
        const iconBtn = (onClick, title, bg, border, color, icon, dataTour) => (
          <button onClick={onClick} title={title} aria-label={title} data-tour={dataTour} style={{ width: 44, height: 34, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", padding: 0, borderRadius: 12, border: "none", background: "transparent", cursor: "pointer", color }}>{icon}</button>
        );
        return (
          // Same blur backdrop as every details panel (DETAIL_BACKDROP: 0.45 tint + 6px blur).
          <Modal onClose={close} blur dim={0.45}>
            <div style={{ background: "var(--c-bg-card)", borderRadius: 20, padding: "20px 20px 18px", width: "min(480px, calc(100vw - 32px))", boxShadow: "0 16px 48px rgba(60,20,80,0.28)", maxHeight: "calc(100dvh - 136px)", overflowY: "auto", fontFamily: "'Quicksand','Nunito',sans-serif" }} onClick={e => e.stopPropagation()}>
              {/* Header */}
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 14 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ width: 36, height: 36, borderRadius: 10, background: `${accentColor}22`, border: `1.5px solid ${accentColor}55`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <HabitIcon h={h} size={20} color={accentColor} />
                  </div>
                  <div>
                    <p style={{ margin: 0, fontSize: 15, fontWeight: 800, color: "var(--c-text)", lineHeight: 1.2, maxWidth: 220 }}>{h.name}</p>
                  </div>
                </div>
                <button onClick={close} style={{ background: "var(--c-bg-soft)", border: "none", borderRadius: 8, width: 28, height: 28, cursor: "pointer", color: "var(--c-text-soft)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>✕</button>
              </div>

              {/* Detail rows */}
              <div style={{ display: "flex", flexDirection: "column" }}>
                <HDMRow label="Frequency" value={freqLabel} />
                <HDMRow label="Type" value={typeLabel} />
                {countedDetail && <HDMRow label="Target completion" value={countedDetail} />}
                {limitDetail && <HDMRow label="Limit used" value={limitDetail} />}
                {isAmountHabit(h)
                  ? <HDMRow label="Goal" value={formatDailyGoal(h)} />
                  : (h.dailyCount || 1) > 1 && <HDMRow label="Daily target" value={`${h.dailyCount}× per day`} />}
                <HDMRow label="Started" value={h.habitStartDate || h.createdAt?.slice(0,10)} />
                {/* Only where the list actually applies (not counted habits — see hasExcludableDates). */}
                {hasExcludableDates(h) && h.excludedDates?.length > 0 && <HDMRow label="Excluded dates" value={<ExcludedDatesValue dates={h.excludedDates} />} />}
                {h.quitReason && <HDMRow label="Reason" value={h.quitReason} />}
                {h.description && <HDMRow label="Description" value={h.description} />}
                {/* Reminder on/off shortcut — only when this habit has a reminder (on,
                    or switched off here) and repeats on days (daily / specific days).
                    Off = the form's Clear button; future alarms/notifications are
                    cancelled. On restores the same time. */}
                {(() => {
                  const live = habits.find(x => x.id === h.id) || h;
                  if (!canToggleHabitReminder(live) || !(live.reminderTime || live.reminderOffTime)) return null;
                  const on = !!live.reminderTime;
                  const time = live.reminderTime || live.reminderOffTime;
                  const [hh, mm] = time.split(":").map(Number);
                  const timeLabel = new Date(2000, 0, 1, hh, mm).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
                  const kind = live.reminderType === "alarm" ? "Alarm" : "Notification";
                  const toggle = () => setHabits(p => p.map(x => x.id === h.id ? setHabitReminderOn(x, !on) : x));
                  return (
                    <div onClick={toggle} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: "1px solid #f5eef8", cursor: "pointer" }}>
                      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 2 }}>
                        <span style={{ fontSize: 9, fontWeight: 800, color: "var(--c-text-soft)", textTransform: "uppercase", letterSpacing: 0.8 }}>Reminder</span>
                        <span style={{ fontSize: 13, color: on ? "var(--c-text)" : "var(--c-text-soft)", lineHeight: 1.5 }}>{kind} · {timeLabel}{on ? "" : " · off"}</span>
                      </div>
                      <div role="switch" aria-checked={on} aria-label="Reminder on/off" style={{ width: 38, height: 22, borderRadius: 999, background: on ? "linear-gradient(135deg, var(--c-primary-light), var(--c-primary-deep))" : "var(--c-border)", position: "relative", flexShrink: 0, transition: "background 0.2s" }}>
                        <div style={{ position: "absolute", top: 2, left: on ? 18 : 2, width: 18, height: 18, borderRadius: "50%", background: "var(--c-bg-card)", boxShadow: "0 1px 3px rgba(0,0,0,0.2)", transition: "left 0.2s cubic-bezier(0.34,1.56,0.64,1)" }} />
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* Action buttons, icon only: a compact group in the middle rather
                  than four quarter-width slots (Jade, 2026-10-16). */}
              <div style={{ display: "flex", justifyContent: "center", gap: 28, marginTop: 6, marginBottom: -6 }}>
                {iconBtn(() => { openEdit(h); close(); }, "Edit", "linear-gradient(135deg, #f8f0fc, #f0e4f8)", "#e0c8f0", "var(--c-text-mid)", <Pencil size={20} fill="currentColor" fillOpacity={0.22} strokeWidth={2.2} />, "habit-edit-btn")}
                {isSkippedHDM
                  ? iconBtn(() => {
                      const undone = (habitSkips || []).find(s => s.habitId === h.id && s.skippedDate === t);
                      setHabitSkips(p => p.filter(s => !(s.habitId === h.id && s.skippedDate === t)));
                      // Also drop the matching reschedule so the habit stops
                      // being (artificially) due on the day it was moved to.
                      if (undone?.rescheduledTo) {
                        setHabits(p => p.map(x => x.id === h.id ? {
                          ...x,
                          movedOccurrences: (x.movedOccurrences || []).filter(mv => !(mv.from === t && mv.to === undone.rescheduledTo)),
                        } : x));
                      }
                      close();
                    }, "Undo Skip", "linear-gradient(135deg, #f0e8fc, #e8d8f8)", "#c8a8e0", "var(--c-text-mid)", <Undo2 size={20} fill="currentColor" fillOpacity={0.22} strokeWidth={2.2} />)
                  : iconBtn(() => { openSkipFlow(h); close(); }, "Skip today", "linear-gradient(135deg, #f0faf5, #e0f5ea)", "#b5e6c8", "#3B6D11", <SkipForward size={20} fill="currentColor" fillOpacity={0.22} strokeWidth={2.2} />)
                }
                {iconBtn(() => { openPauseModal(h); close(); }, "Pause", "linear-gradient(135deg, #f0f4ff, #e4eafc)", "#c0cef0", "var(--c-text-mid)", <Pause size={20} fill="currentColor" fillOpacity={0.22} strokeWidth={2.2} />)}
                {iconBtn(() => { setConfirmDeleteHabit(h); close(); }, "Delete", "linear-gradient(135deg, #fff4f4, #ffe8e8)", "#f5c0c0", "#e8647a", <Trash2 size={20} fill="currentColor" fillOpacity={0.22} strokeWidth={2.2} />)}
              </div>
            </div>
          </Modal>
        );
      })()}

      {/* ── Skip / "Can't do this today" modal ── */}
      {skipFlow && (() => {
        const { habitName, step } = skipFlow;

        // ── Step 1: Confirm skip + optional date picker ──
        if (step === "confirm") {
          const today = new Date();
          const { y, m } = skipCalMonth;
          const firstDay = new Date(y, m, 1).getDay();
          const daysInMonth = new Date(y, m + 1, 0).getDate();
          const monthName = ["January","February","March","April","May","June","July","August","September","October","November","December"][m];
          const cells = [];
          for (let i = 0; i < firstDay; i++) cells.push(null);
          for (let d = 1; d <= daysInMonth; d++) cells.push(d);
          const todayFmt = todayStr();

          return (
            <Modal onClose={cancelSkip} blur={false} dim={0.6} animate={false}>
              <div style={{ background: "var(--c-bg-card)", borderRadius: 20, padding: "26px 22px 20px", width: "100%", maxWidth: 360, boxShadow: "0 8px 32px rgba(180,80,120,0.18)" }}>
                <ConfirmHeader tone="accent" compact icon={SkipForward} title={`Skip "${habitName}" today?`} subtitle="Pick a day below to reschedule it, or just skip it." />

                {/* Mini calendar */}
                <div style={{ background: "var(--c-bg-card)", borderRadius: 12, padding: "10px 8px", marginBottom: 14, border: "none", boxShadow: SKIP_CAL_SHADOW }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                    <button onClick={() => setSkipCalMonth(prev => { const d = new Date(prev.y, prev.m - 1, 1); return { y: d.getFullYear(), m: d.getMonth() }; })}
                      style={{ background: "none", border: "none", cursor: "pointer", color: "var(--c-text-accent)", fontSize: 16, padding: "0 6px" }}><ChevronLeft size={16} /></button>
                    <span style={{ fontSize: 13, fontWeight: 700, color: "var(--c-text)" }}>{monthName} {y}</span>
                    <button onClick={() => setSkipCalMonth(prev => { const d = new Date(prev.y, prev.m + 1, 1); return { y: d.getFullYear(), m: d.getMonth() }; })}
                      style={{ background: "none", border: "none", cursor: "pointer", color: "var(--c-text-accent)", fontSize: 16, padding: "0 6px" }}><ChevronRight size={16} /></button>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2, textAlign: "center" }}>
                    {["Su","Mo","Tu","We","Th","Fr","Sa"].map(d => (
                      <div key={d} style={{ fontSize: 9, color: "var(--c-text-soft)", fontWeight: 700, paddingBottom: 4 }}>{d}</div>
                    ))}
                    {cells.map((day, idx) => {
                      if (!day) return <div key={`e${idx}`} />;
                      const dateStr = `${y}-${String(m+1).padStart(2,"0")}-${String(day).padStart(2,"0")}`;
                      const isPast = dateStr <= todayFmt;
                      const isSelected = skipFlow.rescheduledTo === dateStr;
                      return (
                        <button key={dateStr} disabled={isPast} onClick={() => setSkipFlow(f => ({ ...f, rescheduledTo: isSelected ? null : dateStr }))}
                          style={{ width: "100%", aspectRatio: "1", borderRadius: 8, border: isSelected ? "2px solid var(--c-primary)" : "1px solid transparent", background: isSelected ? "var(--c-bg-card)" : isPast ? "transparent" : "var(--c-bg-active)", color: isPast ? "#d8c8d8" : isSelected ? "var(--c-text-accent)" : "#4a3040", fontWeight: isSelected ? 700 : 400, fontSize: 12, cursor: isPast ? "not-allowed" : "pointer", padding: 0 }}>
                          {day}
                        </button>
                      );
                    })}
                  </div>
                  {skipFlow.rescheduledTo && (
                    <p style={{ margin: "8px 0 0", fontSize: 11, color: "var(--c-text-accent)", textAlign: "center", fontWeight: 600 }}>
                      📅 Rescheduled to {skipFlow.rescheduledTo}
                    </p>
                  )}
                </div>

                <div style={CONFIRM_STACK}>
                  <button onClick={cancelSkip} style={CONFIRM_CANCEL}>Cancel</button>
                  <button onClick={saveSkip} style={CONFIRM_PRIMARY}>{skipFlow.rescheduledTo ? "Reschedule" : "Skip today"}</button>
                </div>
              </div>
            </Modal>
          );
        }

        return null;
      })()}

      {/* ── TO DO section ── */}
      {(() => {
        // Section header always visible so Manage button is always accessible
        const visibleTodayHabits = todayHabits.filter(h => !isCountDismissedThisPeriod(h));
        const TIME_BUCKETS = [
          { key: "morning",   label: "Morning" },
          { key: "afternoon", label: "Afternoon" },
          { key: "evening",   label: "Evening" },
        ];
        let grouped = [];
        if (habitViewMode === "time") {
          TIME_BUCKETS.forEach(b => {
            const bh = visibleTodayHabits.filter(h => (h.timeOfDay || []).includes(b.key));
            if (bh.length) grouped.push({ key: b.key, timeLabel: b.label, habits: bh });
          });
          const unscheduled = visibleTodayHabits.filter(h => !(h.timeOfDay || []).length);
          if (unscheduled.length) grouped.push({ key: "__unscheduled__", timeLabel: "Unscheduled", habits: unscheduled });
        } else if (habitViewMode === "custom") {
          const orderIndex = new Map(customHabitOrder.map((id, i) => [id, i]));
          const sorted = visibleTodayHabits.slice().sort((a, b) => {
            const ai = orderIndex.has(a.id) ? orderIndex.get(a.id) : Infinity;
            const bi = orderIndex.has(b.id) ? orderIndex.get(b.id) : Infinity;
            return ai - bi;
          });
          if (sorted.length) grouped.push({ key: "__custom__", habits: sorted });
        } else {
          const seen = new Set();
          visibleTodayHabits.forEach(h => {
            const key = h.goalId || "__none__";
            if (!seen.has(key)) { seen.add(key); grouped.push({ key, habits: [] }); }
            grouped.find(g => g.key === key).habits.push(h);
          });
        }
        const handleHabitDragEnd = (event) => {
          const { active, over } = event;
          if (!over || active.id === over.id) return;
          const currentOrder = (grouped[0]?.habits || []).map(h => h.id);
          const oldIndex = currentOrder.indexOf(active.id);
          const newIndex = currentOrder.indexOf(over.id);
          if (oldIndex === -1 || newIndex === -1) return;
          setCustomHabitOrder(arrayMove(currentOrder, oldIndex, newIndex));
        };

        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {/* TO DO section header — pill style */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
              <div style={{
                display: "flex", alignItems: "center", gap: 7,
                background: "linear-gradient(120deg, #e8f0e2 0%, #d6e8d0 100%)",
                border: "1.5px solid #b8d4ac",
                borderRadius: 99,
                padding: "6px 14px 6px 10px",
                boxShadow: "0 3px 12px rgba(90,140,70,0.12), 0 1px 0 rgba(255,255,255,0.7) inset",
              }}>
                <span style={{ fontSize: 12, fontWeight: 800, color: "#3B6D11", textTransform: "uppercase", letterSpacing: "0.08em" }}>To Do</span>
                {todayHabits.length > 0 && <span style={{
                  fontSize: 11, fontWeight: 700, color: "#fff",
                  background: "linear-gradient(135deg, #8bbf5e, #5a8c3c)",
                  borderRadius: 99, padding: "2px 9px",
                  boxShadow: "0 2px 6px rgba(90,140,60,0.3)",
                  marginLeft: 2,
                }}>{todayHabits.length}</span>}
              </div>
              <div style={{ flex: 1 }} />
              {todoHabits.length > 0 && (
                <div ref={viewMenuRef} style={{ position: "relative", marginRight: 6 }}>
                  <button
                    onClick={() => setViewMenuOpen(v => !v)}
                    title="Arrange habits" className="hb-head-btn"
                    style={{
                      display: "flex", alignItems: "center", justifyContent: "center",
                      width: 30, height: 30, borderRadius: 99,
                      border: viewMenuOpen ? "1.5px solid var(--c-primary)" : "1.5px solid var(--c-border-mid)",
                      background: "var(--c-bg-card)",
                      color: viewMenuOpen ? "var(--c-primary)" : "var(--c-text-mid)",
                      cursor: "pointer", fontFamily: "inherit",
                      boxShadow: "0 2px 8px rgba(180,100,140,0.08)",
                      transition: "all 0.15s",
                    }}
                  >
                    <SlidersHorizontal size={14} />
                  </button>
                  {viewMenuOpen && (
                    <div style={{
                      position: "absolute", top: "calc(100% + 6px)", right: 0, zIndex: 60,
                      width: 168, padding: 6, borderRadius: 14,
                      background: "var(--c-bg-card)", border: "1px solid var(--c-border-light)",
                      boxShadow: "0 8px 26px rgba(150,70,120,0.18)",
                    }}>
                      <div style={{ fontSize: 10, fontWeight: 800, color: "var(--c-text-soft)", textTransform: "uppercase", letterSpacing: 0.5, padding: "4px 8px 6px" }}>Arrange by</div>
                      {[
                        { key: "category", label: "By Category" },
                        { key: "time", label: "By Time" },
                        { key: "custom", label: "Custom (drag to reorder)" },
                      ].map(({ key: vk, label }) => {
                        const active = habitViewMode === vk;
                        return (
                          <button key={vk} onClick={() => { setHabitViewMode(vk); setViewMenuOpen(false); }}
                            style={{
                              display: "block", width: "100%", textAlign: "left", fontSize: 12.5,
                              fontWeight: active ? 800 : 600, padding: "8px 8px", borderRadius: 10,
                              border: "none", cursor: "pointer", fontFamily: "inherit",
                              background: active ? "var(--c-bg-active)" : "transparent",
                              color: active ? "var(--c-text-accent)" : "var(--c-text-mid)",
                            }}>
                            {label}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
              <button
                onClick={() => { setManageModal(true); setExpandedManageHabit(null); }}
                title="Manage habits" className="hb-head-btn"
                style={{
                  display: "flex", alignItems: "center", justifyContent: "center",
                  width: 30, height: 30, borderRadius: 99,
                  border: "1.5px solid var(--c-border-mid)",
                  background: "var(--c-bg-card)",
                  color: "var(--c-text-mid)",
                  cursor: "pointer", fontFamily: "inherit",
                  boxShadow: "0 2px 8px rgba(180,100,140,0.08)",
                  transition: "all 0.15s",
                  marginRight: 6,
                }}
              >
                <Settings size={14} />
              </button>
              <button
                className="habits-stats-btn hb-head-btn"
                onClick={() => setShowStats(true)}
                title="Habit Stats"
                style={{
                  display: "flex", alignItems: "center", gap: 4,
                  padding: "6px 13px", borderRadius: 99,
                  border: "1.5px solid var(--c-border-mid)",
                  background: "var(--c-bg-card)",
                  color: "var(--c-text-mid)", fontSize: 14, fontWeight: 700,
                  cursor: "pointer", fontFamily: "inherit",
                  boxShadow: "0 2px 8px rgba(180,100,140,0.08)",
                  transition: "all 0.15s",
                }}
              >
                <BarChart2 size={11} /> Habit Stats
              </button>
            </div>

            {todoHabits.length === 0 ? <p style={{ ...S.empty, fontWeight: 800, color: isDarkBgTheme() ? "#ffffff" : "#000000" }}>Tap + to add your first habit ^^</p> :
             todayHabits.length === 0 ? <p style={{ ...S.empty, fontWeight: 800, color: isDarkBgTheme() ? "#ffffff" : "#000000" }}>No habits due today</p> : null}
            {(() => {
              const renderedGroups = grouped.map(({ key, habits: groupHabits, timeLabel }) => {
              const cat = habitViewMode === "category" && key !== "__none__" ? categories.find(c => c.id === key) : null;
              let globalIdx = 0; // for borderTop logic within each group
              return (
                <div key={key}>
                  {/* ── Group label header (category name, time-of-day bucket, or
                      "Uncategorized" — suppressed entirely in Custom view, which
                      is one flat drag-reorderable list) ── */}
                  {cat ? (
                    <div style={{
                      display: "flex", alignItems: "center", gap: 8,
                      padding: "5px 4px 4px 0",
                    }}>
                      <span style={{
                        fontSize: 11, fontWeight: 800, color: isDarkBgTheme() ? "#ffffff" : "#000000",
                        letterSpacing: "0.04em", textTransform: "uppercase",
                      }}>
                        {cat.title}
                      </span>
                      <span style={{ fontSize: 10, fontWeight: 700, color: isDarkBgTheme() ? "#ffffff" : "#000000", opacity: 0.55 }}>
                        {groupHabits.length}
                      </span>
                      <div style={{ flex: 1 }} />
                    </div>
                  ) : habitViewMode === "time" ? (
                    <div style={{
                      display: "flex", alignItems: "center", gap: 8,
                      padding: "5px 4px 4px 2px",
                    }}>
                      <div style={{
                        width: 22, height: 22, borderRadius: "50%",
                        background: "#ffffff", flexShrink: 0,
                        display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12,
                      }}>
                        {key === "morning" ? "🌅" : key === "afternoon" ? "☀️" : key === "evening" ? "🌙" : "🕓"}
                      </div>
                      <span style={{
                        fontSize: 11, fontWeight: 800, color: isDarkBgTheme() ? "#ffffff" : "#000000",
                        letterSpacing: "0.04em", textTransform: "uppercase",
                      }}>
                        {timeLabel}
                      </span>
                      <span style={{ fontSize: 10, fontWeight: 700, color: isDarkBgTheme() ? "#ffffff" : "#000000", opacity: 0.55 }}>
                        {groupHabits.length}
                      </span>
                      <div style={{ flex: 1 }} />
                    </div>
                  ) : habitViewMode === "category" && grouped.length > 1 ? (
                    // Only show the "Uncategorized" label if there are also categorized groups
                    <div style={{
                      display: "flex", alignItems: "center", gap: 8,
                      padding: "5px 4px 4px 2px",
                    }}>
                      <div style={{
                        width: 22, height: 22, borderRadius: "50%",
                        background: "#c0b0b8", flexShrink: 0,
                        display: "flex", alignItems: "center", justifyContent: "center",
                      }} />
                      <span style={{
                        fontSize: 11, fontWeight: 800, color: isDarkBgTheme() ? "#ffffff" : "#000000",
                        letterSpacing: "0.04em", textTransform: "uppercase",
                      }}>
                        Uncategorized
                      </span>
                      <span style={{ fontSize: 10, fontWeight: 700, color: isDarkBgTheme() ? "#ffffff" : "#000000", opacity: 0.55 }}>
                        {groupHabits.length}
                      </span>
                      <div style={{ flex: 1 }} />
                    </div>
                  ) : null}

                  {/* ── Habits in this group ── hb-group: on the Default background the
                      outline becomes a soft shadow (Jade, 2026-10-17; CSS in App.jsx). */}
                  <div className={habitViewMode === "custom" ? undefined : "hb-group hb-card"} style={ habitViewMode === "custom"
                    ? { display: "flex", flexDirection: "column", gap: 8 }
                    : { border: `1.5px solid ${cat ? (themeTone(cat.color) + "44") : "var(--c-border-light)"}`, borderRadius: 18, overflow: "hidden", background: "var(--c-bg-card)", boxShadow: `0 4px 16px ${cat ? (themeTone(cat.color) + "14") : "rgba(180,80,120,0.06)"}, 0 1px 0 rgba(255,255,255,0.6) inset` }
                  }>
                    {groupHabits.map((h, _i) => {
              const isSkipped = habitSkips?.some(s => s.habitId === h.id && s.skippedDate === t);
              const isMonthlyHabit = h.frequencyType === "monthly";
              const isMonthlyCount = isMonthlyHabit && h.monthlyMode === "count";
              const isDueToday = !isMonthlyHabit || isMonthlyCount || (h.monthDates || []).includes(new Date(t + "T00:00:00").getDate());
              const target = h.dailyCount || 1;
              // A unit habit (steps, glasses, …) logs an amount that may pass its
              // goal; a Count habit's taps stop at the goal, as they always did.
              const isAmount = isAmountHabit(h);
              const current = isAmount
                ? loggedAmount(h, habitLogs[t]?.[h.id])
                : Math.min(typeof habitLogs[t]?.[h.id] === "number" ? habitLogs[t][h.id] : (habitLogs[t]?.[h.id] ? 1 : 0), target);
              const done = current >= target;
              const nextDueDate = isMonthlyHabit && !isDueToday
                ? (h.monthDates || []).filter(d => d > new Date(t + "T00:00:00").getDate()).sort((a,b) => a-b)[0]
                : null;
              const { score, totalDays } = getConsistencyScore(h, habitLogs, habitSkips);
              const scoreColor = score >= 80 ? "#3B6D11" : score >= 50 ? "#BA7517" : "#A32D2D";
              const circ = 2 * Math.PI * 14; const fill = (score / 100) * circ;
              const skipNotes = (habitSkips || []).filter(s => s.habitId === h.id && s.note && s.note.trim()).length;
              const hasEverSkipped = (habitSkips || []).some(s => s.habitId === h.id);

              const isCountHabit = h.frequencyType === "count";

              // ── Count habit: weekly/monthly progress ──
              const countPeriodDone = (() => {
                if (!isCountHabit) return 0;
                // frequencyType === "count" is always the weekly-count mode — the
                // separate "Monthly > Times/month" mode uses frequencyType ===
                // "monthly" instead, so this block never needs a monthly period.
                const periodLen = 7;
                const habitOrigin = h.habitStartDate || localDateStr(h.createdAt) || t;
                const originMs = new Date(habitOrigin + "T00:00:00").getTime();
                const todayMs = new Date(t + "T00:00:00").getTime();
                const periodIdx = Math.max(0, Math.floor((todayMs - originMs) / (periodLen * 86400000)));
                const periodStartMs = originMs + periodIdx * periodLen * 86400000;
                let count = 0;
                for (let i = 0; i < periodLen; i++) {
                  const key = fmt(new Date(periodStartMs + i * 86400000));
                  if (key > t) break;
                  if (dayCountsTowardPeriod(h, habitLogs[key]?.[h.id], key)) count++;
                }
                return count;
              })();
              const countPeriodTarget = isCountHabit ? (h.weeklyCount || 1) : 0;
              const countPeriodHit = isCountHabit && countPeriodDone >= countPeriodTarget;
              // today's log count for the counter pill
              const todayCountVal = isCountHabit ? (typeof habitLogs[t]?.[h.id] === "number" ? habitLogs[t][h.id] : (habitLogs[t]?.[h.id] ? 1 : 0)) : 0;

              const isRowExiting = !!exitingCountHabits[h.id];
              const isCustomView = habitViewMode === "custom";
              return (
                <SortableHabitRow key={h.id} id={h.id} enabled={isCustomView}>
                <div className={"clarity-row-item" + (isCustomView ? " hb-card" : "")} data-tour={h.id === firstHabitCardId ? "first-habit-card" : undefined} style={{
                  // hb-card on a gradient background replaces the background below
                  // with a see-through one; --hb-tint keeps the done / target-hit tint on it.
                  "--hb-tint": isCountHabit ? (countPeriodHit ? `${themeTone(cat?.color || h.color)}12` : undefined) : (done ? `${themeTone(cat?.color || h.color)}18` : undefined),
                  ...(isCustomView
                    ? { borderRadius: 16, boxShadow: "0 3px 12px rgba(180,80,120,0.08), 0 1px 0 rgba(255,255,255,0.6) inset" }
                    : { borderTop: _i === 0 ? "none" : `1px solid ${cat ? (themeTone(cat.color) + "33") : "var(--c-border-light)"}` }),
                  background: isCountHabit
                    ? (countPeriodHit ? `${themeTone(cat?.color || h.color)}12` : (isCustomView ? "var(--c-bg-card)" : "transparent"))
                    : (done ? `${themeTone(cat?.color || h.color)}18` : (isCustomView ? "var(--c-bg-card)" : "transparent")),
                  padding: isCustomView ? "10px 12px 10px 26px" : "10px 12px",
                  animationDelay: `${_i * 0.04}s`,
                  // Completion burst+slide (counted habits only) — mirrors the
                  // task list's exit animation when a period target is fully hit.
                  opacity: isRowExiting ? 0 : 1,
                  transform: isRowExiting ? "translateX(110%) scale(0.94)" : "translateX(0) scale(1)",
                  transition: isRowExiting ? "transform 0.38s cubic-bezier(0.4,0,0.2,1), opacity 0.34s ease" : "background 0.2s",
                }}>
                  {/* Main row — tappable to open detail panel */}
                  <div
                    onClick={() => setHabitDetailModal(h)}
                    style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", userSelect: "none" }}
                  >
                    <HabitIcon h={h} size={18} color={cat?.color} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <span className="hb-name" style={{ fontSize: 15, color: "var(--c-text)", fontWeight: 700, textDecoration: done ? "line-through" : "none", opacity: done ? 0.6 : 1 }}>{h.name}</span>
                      {!isAmount && (h.dailyCount || 1) > 1 && <span style={{ fontSize: 10, color: "var(--c-text-soft)", marginLeft: 5 }}>({h.dailyCount}x/day)</span>}
                      {h.endDate && (() => {
                        const daysLeft = Math.ceil((new Date(h.endDate + "T00:00:00") - new Date(t + "T00:00:00")) / 86400000);
                        const label = daysLeft === 0 ? "ends today 🏁" : daysLeft === 1 ? "ends tomorrow" : `ends ${h.endDate}`;
                        return (
                          <div style={{ fontSize: 10, color: "#a040c8", marginTop: 1, fontWeight: 600 }}>{label}</div>
                        );
                      })()}
                    </div>

                    {/* Skipped label in row */}
                    {isSkipped && (
                      <span style={{ fontSize: 11, color: "#9a70a0", flexShrink: 0 }}>⏭ skipped</span>
                    )}

                    {/* Checkbox / Counter pill — only when not skipped */}
                    {!isSkipped && (() => {
                      const countInfo = getCountPeriodInfo(h);
                      const showCountDismiss = countInfo && countInfo.hit && !isCountDismissedThisPeriod(h);
                      return (
                      <div onClick={e => e.stopPropagation()} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        {/* Period's due date, plus the compact "<done>/<target>" counter
                            right beside the checkbox (full detail still lives in the
                            habit's detail panel too). These labels sit inside the
                            controls wrapper (which stops taps reaching the row), so
                            they open the detail panel themselves — only the buttons
                            and checkbox should do something else. */}
                        {countInfo && !countInfo.hit && (
                          <span onClick={() => setHabitDetailModal(h)} style={{ cursor: "pointer", fontSize: 10, color: "var(--c-text-soft)", fontStyle: "italic", whiteSpace: "nowrap", flexShrink: 0 }}>
                            due {countInfo.dueLabel}
                          </span>
                        )}
                        {countInfo && (
                          <span onClick={() => setHabitDetailModal(h)} className={"hb-count-label" + (countInfo.hit ? " is-hit" : "")} style={{ cursor: "pointer", fontSize: 11, fontWeight: 700, color: countInfo.hit ? "#3B6D11" : readableOnWhite(cat?.color || h.color || "#d4789a"), "--cnt-c-base": vividPastel(cat?.color || h.color || "#d4789a"), flexShrink: 0 }}>
                            {countInfo.done}/{countInfo.target}
                          </span>
                        )}
                        {/* Once the period target is fully hit, offer a tap to clear
                            it from the daily list for the rest of this period. */}
                        {showCountDismiss && (
                          <button onClick={() => setConfirmDismissCount(h)} title="Clear from today's list" className="hb-count-dismiss"
                            // minHeight/minWidth 0: the app-wide 36px button floor would
                            // otherwise make this taller than the 28px checkbox and
                            // grow the card the moment the target is hit.
                            style={{ flexShrink: 0, width: 28, height: 28, minHeight: 0, minWidth: 0, background: "transparent", border: "none", color: "#3B6D11", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 0 }}>
                            <MinusCircle size={20} weight="bold" />
                          </button>
                        )}
                        {isAmount && (isCountHabit || isDueToday) ? (
                          /* Unit habit — the Add amount sheet does the logging. Once
                             the goal is met it shows ticked; tapping still opens the
                             sheet (never clears 10,000 steps in one tap). */
                          done ? (
                            <HabitCheckbox checked={true} burst={false} color={vividPastel(cat?.color || h.color)} onChange={() => setAmountSheet({ habitId: h.id, date: t })} />
                          ) : (
                            <AmountButton habit={h} current={current} goal={target} color={vividPastel(cat?.color || h.color)} dashed={isCountHabit}
                              onOpen={() => setAmountSheet({ habitId: h.id, date: t })} />
                          )
                        ) : isCountHabit ? (
                          <HabitCheckbox
                            dashed
                            checked={todayCountVal > 0}
                            color={vividPastel(cat?.color || h.color)}
                            onChange={() => logHabit(h.id, t, todayCountVal > 0 ? 0 : 1)}
                          />
                        ) : !isDueToday ? (
                          /* Monthly habit — not due today, show next due date */
                          <span onClick={() => setHabitDetailModal(h)} style={{ cursor: "pointer", fontSize: 10, color: "var(--c-text-soft)", fontStyle: "italic", whiteSpace: "nowrap" }}>
                            {nextDueDate ? `due ${nextDueDate}${["st","nd","rd"][((nextDueDate+90)%100-10)%10-1]||"th"}` : "done this month"}
                          </span>
                        ) : target > 1 ? (
                          done ? (
                            <HabitCheckbox checked={true} color={vividPastel(cat?.color || h.color)} onChange={() => logHabit(h.id, t, 0)} />
                          ) : (
                            <MultiCountButton
                              current={current}
                              target={target}
                              color={vividPastel(cat?.color || h.color)}
                              onIncrement={() => logHabit(h.id, t, Math.min(target, current + 1))}
                            />
                          )
                        ) : (
                          <HabitCheckbox
                            checked={!!habitLogs[t]?.[h.id]}
                            color={vividPastel(cat?.color || h.color)}
                            onChange={() => logHabit(h.id, t, habitLogs[t]?.[h.id] ? 0 : 1)}
                          />
                        )}
                      </div>
                      );
                    })()}

                  </div>

                </div>
                </SortableHabitRow>
              );
            })}
                  </div>
                </div>
              );
              });
              if (habitViewMode !== "custom") return renderedGroups;
              return (
                <DndContext sensors={habitDndSensors} collisionDetection={closestCenter} onDragEnd={handleHabitDragEnd}>
                  <SortableContext items={(grouped[0]?.habits || []).map(h => h.id)} strategy={verticalListSortingStrategy}>
                    {renderedGroups}
                  </SortableContext>
                </DndContext>
              );
            })()}
          </div>
        );
      })()}

      {/* ── BOUNDARIES section header ── pill style (honest but kind) */}
      {restrictedHabits.length > 0 && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 14 }}>
          <div style={{
            display: "flex", alignItems: "center", gap: 7,
            background: "linear-gradient(120deg, #fff0e0 0%, #ffe4c8 100%)",
            border: "1.5px solid #f0c890",
            borderRadius: 99,
            padding: "6px 14px 6px 10px",
            boxShadow: "0 3px 12px rgba(210,130,20,0.12), 0 1px 0 rgba(255,255,255,0.7) inset",
          }}>
            <span style={{ fontSize: 12, fontWeight: 800, color: "#B25A00", textTransform: "uppercase", letterSpacing: "0.08em" }}>restricted</span>
            <span style={{
              fontSize: 11, fontWeight: 700, color: "#fff",
              background: "linear-gradient(135deg, #f5a623, #e0821c)",
              borderRadius: 99, padding: "2px 9px",
              boxShadow: "0 2px 6px rgba(210,130,20,0.3)",
              marginLeft: 2,
            }}>{restrictedHabits.length}</span>
          </div>
        </div>
      )}



      {/* ── RESTRICTED habits — grouped by category (same pattern as To Do) ── */}
      {restrictedHabits.length > 0 && (() => {
        const grouped = [];
        const seen = new Set();
        restrictedHabits.forEach(h => {
          const key = h.goalId || "__none__";
          if (!seen.has(key)) { seen.add(key); grouped.push({ key, habits: [] }); }
          grouped.find(g => g.key === key).habits.push(h);
        });

        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {grouped.map(({ key, habits: groupHabits }) => {
              const cat = key !== "__none__" ? categories.find(c => c.id === key) : null;
              return (
                <div key={key}>
                  {/* Category label */}
                  {cat ? (
                    <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 4px 4px 2px" }}>
                      <div style={{
                        width: 22, height: 22, borderRadius: "50%",
                        background: "#ffffff", flexShrink: 0,
                        display: "flex", alignItems: "center", justifyContent: "center",
                      }}>
                        <CategoryIcon cat={cat} size={12} color={cat.color || "#d4789a"} />
                      </div>
                      <span style={{ fontSize: 11, fontWeight: 800, color: isDarkBgTheme() ? "#ffffff" : "#000000", letterSpacing: "0.04em", textTransform: "uppercase" }}>
                        {cat.title}
                      </span>
                      <span style={{ fontSize: 10, fontWeight: 700, color: isDarkBgTheme() ? "#ffffff" : "#000000", opacity: 0.55 }}>
                        {groupHabits.length}
                      </span>
                      <div style={{ flex: 1 }} />
                    </div>
                  ) : grouped.length > 1 ? (
                    <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 4px 4px 2px" }}>
                      <div style={{ width: 22, height: 22, borderRadius: "50%", background: "#c0b0b8", flexShrink: 0 }} />
                      <span style={{ fontSize: 11, fontWeight: 800, color: isDarkBgTheme() ? "#ffffff" : "#000000", letterSpacing: "0.04em", textTransform: "uppercase" }}>Uncategorized</span>
                      <span style={{ fontSize: 10, fontWeight: 700, color: isDarkBgTheme() ? "#ffffff" : "#000000", opacity: 0.55 }}>{groupHabits.length}</span>
                      <div style={{ flex: 1 }} />
                    </div>
                  ) : null}

                  <div className="hb-group hb-card" style={{ border: `1.5px solid ${cat ? (cat.color + "44") : "#f0c8d8"}`, borderRadius: 18, overflow: "hidden", background: "var(--c-bg-card)", boxShadow: `0 4px 16px ${cat ? (cat.color + "12") : "rgba(192,57,43,0.06)"}, 0 1px 0 rgba(255,255,255,0.6) inset` }}>
                    {groupHabits.map((h, _i) => {
                      const isSkipped = habitSkips?.some(s => s.habitId === h.id && s.skippedDate === t);
                      const slipNotes = (h.relapseLog || []).filter(r => r.note && r.note.trim()).length;
                      const skipNotes = (habitSkips || []).filter(s => s.habitId === h.id && s.note && s.note.trim()).length;
                      const totalNotes = slipNotes + skipNotes;

                      // ── LIMITED ──
                      if (h.habitType === "limited") {
                        if (h.allowanceMode) return renderBudgetCard(h, cat, _i);
                        const allowed = isAllowedToday(h);
                        const instances = getTodayInstances(h);
                        const maxInst = h.maxTimesPerDay || 1;
                        const overLimit = instances >= maxInst;
                        const slippedToday = (h.relapseLog || []).some(r => r.date === t);
                        const limitedTone = chipTone({ slippedToday });

                        return (
                          <div key={h.id} className="clarity-row-item" data-tour={h.id === firstHabitCardId ? "first-habit-card" : undefined} style={{
                            borderTop: _i === 0 ? "none" : `1px solid ${cat ? (cat.color + "33") : "#f0d8ea"}`,
                            background: "transparent", padding: "10px 12px", animationDelay: `${_i * 0.04}s`, transition: "background 0.2s",
                          }}>
                            {/* Main row */}
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <div onClick={() => setHabitDetailModal(h)} style={{ cursor: "pointer", flexShrink: 0 }}>
                                <HabitIcon h={h} size={18} color={cat?.color} />
                              </div>
                              <div onClick={() => setHabitDetailModal(h)} style={{ flex: 1, minWidth: 0, cursor: "pointer", userSelect: "none" }}>
                                <span style={{ fontSize: 15, color: "var(--c-text)", fontWeight: 700 }}>{h.name}</span>
                                {/* Condensed single info line — only rendered when there's
                                    actually something to show, so this card's collapsed
                                    height matches a To Do card's (an always-present empty
                                    wrapper here used to add extra height every row). */}
                                {h.habitType === "limited" && h.limitedInterval && !h.allowanceMode ? (
                                  <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 3, flexWrap: "wrap" }}>
                                    <span style={{ fontSize: 10, color: "#c0a860" }}>🚫 {intervalLabel(h)}</span>
                                  </div>
                                ) : limitedOffDays(h).length > 0 && (
                                  <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 3, flexWrap: "wrap" }}>
                                    <span style={{ fontSize: 10, color: "#c0a860" }}>🚫 {limitedOffDays(h).sort((a,b)=>a-b).map(i => ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"][i]).join(", ")}</span>
                                  </div>
                                )}
                                {h.endDate && (() => { const dl = Math.ceil((new Date(h.endDate + "T00:00:00") - new Date(t + "T00:00:00")) / 86400000); return <div style={{ fontSize: 10, color: "#a040c8", marginTop: 1, fontWeight: 600 }}>{dl === 0 ? "ends today 🏁" : dl === 1 ? "ends tomorrow" : `ends ${h.endDate}`}</div>; })()}
                              </div>
                              {/* Right side: slipped pill or dots + shield */}
                              {isSkipped && <span style={{ fontSize: 11, color: "#9a70a0", flexShrink: 0 }}>⏭️ skipped</span>}
                              {!isSkipped && slippedToday && (
                                // Tapping the yellow chip again undoes today's slip (Jade, 2026-10-26).
                                <ShieldChip days={0} tone={limitedTone} title="Slipped today. Tap to undo"
                                  onClick={e => { e.stopPropagation(); setHabits(p => p.map(x => x.id === h.id ? { ...x, relapseLog: (x.relapseLog || []).filter(r => r.date !== t) } : x)); }} />
                              )}
                              {!isSkipped && !slippedToday && (
                                <ShieldChip days={cleanDays(h, t)} tone={limitedTone} tour="limited-chip"
                                  title={`${cleanDays(h, t)} days in a row within your limit. Tap to log a slip`}
                                  onClick={e => {
                                    e.stopPropagation();
                                    setRelapseModal({ id: h.id, context: !allowed ? "not-allowed" : overLimit ? "over-limit" : undefined });
                                  }} />
                              )}
                            </div>

                            {/* Instance log row — compact */}
                            {!isSkipped && !slippedToday && allowed && (
                              <div style={{ marginTop: 5, display: "flex", alignItems: "center", gap: 8 }}>
                                <span style={{ fontSize: 10, color: "#c09060" }}>{instances}/{maxInst} today</span>
                                {!overLimit && <button onClick={() => logHabit(h.id, t, instances + 1)} style={{ fontSize: 10, padding: "2px 9px", borderRadius: 8, border: "1px solid #f5c842", background: "var(--c-bg-soft)", color: "#c27d20", cursor: "pointer" }}>+ Log</button>}
                                {instances > 0 && !overLimit && <button onClick={() => logHabit(h.id, t, instances - 1)} style={{ fontSize: 10, color: "#b09080", background: "transparent", border: "none", cursor: "pointer" }}>undo</button>}
                              </div>
                            )}

                          </div>
                        );
                      }

                      // ── NOTTODO (Boundary) ──
                      const slippedToday = h.relapseLog?.some(r => r.date === t);

                      return (
                        <div key={h.id} className="clarity-row-item" data-tour={h.id === firstHabitCardId ? "first-habit-card" : undefined} style={{
                          borderTop: _i === 0 ? "none" : `1px solid ${cat ? (cat.color + "33") : "#f0d8ea"}`,
                          background: slippedToday ? "#fbf6f8" : "transparent", padding: "10px 12px", animationDelay: `${_i * 0.04}s`, transition: "background 0.2s",
                        }}>

                          {/* Main row */}
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <div onClick={() => setHabitDetailModal(h)} style={{ cursor: "pointer", flexShrink: 0 }}>
                              <HabitIcon h={h} size={18} color={cat?.color} />
                            </div>

                            <div onClick={() => setHabitDetailModal(h)} style={{ flex: 1, minWidth: 0, cursor: "pointer", userSelect: "none" }}>
                              <div style={{ fontSize: 15, color: "var(--c-text)", fontWeight: 700 }}>{h.name}</div>
                              {h.quitReason && <div style={{ fontSize: 11, color: "var(--c-text-soft)", marginTop: 2, fontStyle: "italic", display: "flex", alignItems: "center", gap: 4 }}><Heart size={10} style={{ flexShrink: 0 }} />{h.quitReason}</div>}
                              {h.endDate && (() => { const dl = Math.ceil((new Date(h.endDate + "T00:00:00") - new Date(t + "T00:00:00")) / 86400000); return <div style={{ fontSize: 10, color: "#a040c8", marginTop: 1, fontWeight: 600 }}>{dl === 0 ? "ends today 🏁" : dl === 1 ? "ends tomorrow" : `ends ${h.endDate}`}</div>; })()}
                            </div>

                            {!isSkipped && slippedToday && (
                              // Tapping the yellow chip again undoes today's slip (Jade, 2026-10-26).
                              <ShieldChip days={0} tone="slipped" title="Slipped today. Tap to undo"
                                onClick={e => { e.stopPropagation(); clearTodayMark(h); }} />
                            )}
                            {isSkipped && <span style={{ fontSize: 11, color: "#9a70a0", flexShrink: 0 }}>⏭️ skipped</span>}
                            {!isSkipped && !slippedToday && (
                              <ShieldChip days={cleanDays(h, t)} tone="clean" tour="fullstop-chip"
                                title={`${cleanDays(h, t)} days in a row without it. Tap to log a slip`}
                                onClick={e => { e.stopPropagation(); setRelapseModal(h.id); }} />
                            )}
                          </div>

                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })()}

      {/* ── LONG-TERM / HIDDEN HABITS DRAWER ── */}
      {/* Dismissing a completed yearly-counted habit for the rest of its year
          (see the MinusCircle button below) must also hide the drawer itself
          — and drop its badge count — the moment the last one is cleared, not
          just hide it from the list inside an otherwise-still-visible drawer.
          It reappears automatically next year once isCountDismissedThisPeriod
          rolls over to the new period. */}
      {(() => { const visibleHiddenHabits = hiddenHabits.filter(h => !isCountDismissedThisPeriod(h)); return visibleHiddenHabits.length > 0 && (
        <div style={{ marginTop: 10, border: "1.5px solid var(--c-border)", borderRadius: 14, overflow: "hidden", background: "var(--c-bg-card)" }}>
          <button onClick={() => setHiddenDrawerOpen(o => !o)}
            style={{ width: "100%", display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", background: "transparent", border: "none", cursor: "pointer", fontFamily: "inherit", textAlign: "left" }}>
            <span style={{ fontSize: 13 }}>🗓️</span>
            <span style={{ flex: 1, fontSize: 12, fontWeight: 800, color: "var(--c-text-accent)", textTransform: "uppercase", letterSpacing: "0.06em" }}>Yearly Habits</span>
            <span style={{ fontSize: 11, fontWeight: 700, color: "var(--c-on-primary)", background: "linear-gradient(135deg, var(--c-primary-light), var(--c-primary-deep))", borderRadius: 99, padding: "2px 9px" }}>{visibleHiddenHabits.length}</span>
            <ChevronRight size={16} style={{ color: "var(--c-text-soft)", transform: hiddenDrawerOpen ? "rotate(90deg)" : "none", transition: "transform 0.2s" }} />
          </button>
          {hiddenDrawerOpen && (
            <div style={{ borderTop: "1px solid var(--c-border)" }}>
              {visibleHiddenHabits.map((h, _i) => {
                const isYearlyCount = h.frequencyType === "yearly" && (h.yearlyMode || "count") === "count";
                const startStr = h.habitStartDate || localDateStr(h.createdAt) || t;
                const dueStr = habitYearlyDueDate(h);
                const target = isYearlyCount ? Math.max(1, parseInt(h.yearlyCount, 10) || 1) : 1;
                const completed = Object.keys(habitLogs).filter(d => d >= startStr && d <= dueStr && dayCountsTowardPeriod(h, habitLogs[d]?.[h.id], d)).length;
                const isAmount = isAmountHabit(h);
                const doneToday = isAmount ? dayCountsTowardPeriod(h, habitLogs[t]?.[h.id], t) : !!habitLogs[t]?.[h.id];
                const dueLabel = (() => { const d = new Date(dueStr + "T00:00:00"); return `${["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`; })();
                const yhCat = h.goalId ? categories.find(c => c.id === h.goalId) : null;
                const yearlyHit = isYearlyCount && completed >= target;
                return (
                  <div key={h.id} className="clarity-row-item" style={{ borderTop: _i === 0 ? "none" : "1px solid var(--c-border)", padding: "10px 14px", display: "flex", alignItems: "center", gap: 10 }}>
                    <div onClick={() => setHabitDetailModal(h)} style={{ cursor: "pointer", flexShrink: 0 }}><HabitIcon h={h} size={18} color={yhCat?.color} /></div>
                    <div onClick={() => setHabitDetailModal(h)} style={{ flex: 1, minWidth: 0, cursor: "pointer" }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: "var(--c-text)" }}>{h.name}</div>
                      {isYearlyCount && (
                        <div style={{ fontSize: 10, color: "var(--c-text-soft)", marginTop: 1 }}>
                          {completed}/{target} this year · due {dueLabel}
                        </div>
                      )}
                    </div>
                    {isYearlyCount ? (
                      <div onClick={e => e.stopPropagation()} style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                        {yearlyHit && (
                          <button onClick={() => setConfirmDismissCount(h)} title="Clear. Comes back automatically next year"
                            style={{ width: 28, height: 28, minHeight: 0, minWidth: 0, background: "transparent", border: "none", color: "#3B6D11", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 0 }}>
                            <MinusCircle size={20} weight="bold" />
                          </button>
                        )}
                        <button onClick={() => (isAmount ? setAmountSheet({ habitId: h.id, date: t }) : logHabit(h.id, t, doneToday ? 0 : 1))}
                          style={{ fontSize: 11, fontWeight: 700, padding: "5px 12px", borderRadius: 99, border: `1.5px solid ${doneToday ? "#c8f0d0" : "#f0d0e8"}`, background: doneToday ? "#f0fff4" : "var(--c-bg-soft)", color: doneToday ? "#3a7050" : "var(--c-text-accent)", cursor: "pointer", fontFamily: "inherit" }}>
                          {doneToday ? "✓ Logged today" : isAmount && habitLogs[t]?.[h.id] ? `+ ${formatProgress(h, loggedAmount(h, habitLogs[t][h.id]))}` : "+ Log"}
                        </button>
                      </div>
                    ) : (
                      <span style={{ fontSize: 10, color: "var(--c-text-soft)", fontStyle: "italic", flexShrink: 0 }}>hidden</span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ); })()}

      {/* ── MANAGE ALL HABITS BOTTOM SHEET ── */}
      {/* ── PAUSE HABIT MODAL ── */}
      {pauseModal && (() => {
        const closePause = () => { setPauseModal(null); setPauseUntilDate(""); setPauseReason(""); };
        const canPause = pauseMode === "forever" || !!pauseUntilDate;
        const PRESETS = [{ label: "1 week", days: 7 }, { label: "2 weeks", days: 14 }, { label: "1 month", days: 30 }];
        const chip = (on) => ({
          padding: "10px 16px", borderRadius: 999, fontFamily: "inherit", fontSize: 14, fontWeight: 700, cursor: "pointer",
          border: `2px solid ${on ? "var(--c-primary)" : "var(--c-border)"}`,
          background: on ? "var(--c-bg-active)" : "var(--c-bg-card)",
          color: on ? "var(--c-text-accent)" : "var(--c-text-mid)",
        });
        const longDate = (ds) => new Date(ds + "T00:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
        // Live result line, so the outcome is clear before confirming.
        let summary = null;
        if (pauseMode === "forever") summary = "Paused until you resume it yourself.";
        else if (pauseUntilDate) {
          const resume = new Date(pauseUntilDate + "T00:00:00"); resume.setDate(resume.getDate() + 1);
          const days = Math.round((new Date(pauseUntilDate + "T00:00:00") - new Date(t + "T00:00:00")) / 86400000) + 1;
          summary = `Resumes ${longDate(fmt(resume))} · ${days} day${days !== 1 ? "s" : ""} paused`;
        }
        return createPortal(
          <div onClick={pauseSheet.dismiss} className="no-nav-blur" style={{ position: "fixed", inset: 0, zIndex: 9000, display: "flex", alignItems: "flex-end", justifyContent: "center", fontFamily: "'Quicksand', 'Nunito', sans-serif" }}>
            <style>{`@keyframes pauseSheetUp{from{transform:translateY(100%)}to{transform:translateY(0)}}`}</style>
            {/* The dim is its own layer so a swipe down can fade it without fading the sheet. */}
            <div aria-hidden ref={pauseSheet.dimRef} style={{ position: "absolute", inset: 0, background: "rgba(60,30,50,0.45)" }} />
            <div ref={pauseSheet.sheetRef} onClick={e => e.stopPropagation()} style={{ position: "relative", width: "100%", maxWidth: 480, boxSizing: "border-box", background: "var(--c-bg-active)", borderRadius: "32px 32px 0 0", padding: "10px 22px calc(22px + env(safe-area-inset-bottom, 0px))", boxShadow: "0 -8px 40px var(--c-shadow)", animation: "pauseSheetUp 0.22s ease-out" }}>
              {/* Drag handle */}
              <div style={{ width: 40, height: 4, borderRadius: 4, background: "var(--c-border)", margin: "0 auto 18px" }} />
              {/* Quick durations + custom date + indefinite */}
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "center" }}>
                {PRESETS.map(({ label, days }) => {
                  const on = pauseMode === "date" && !pauseCustom && pauseUntilDate === pauseDateIn(days);
                  return <button key={label} type="button" onClick={() => { setPauseMode("date"); setPauseCustom(false); setPauseUntilDate(pauseDateIn(days)); }} style={chip(on)}>{label}</button>;
                })}
                <button type="button" onClick={() => { setPauseMode("date"); setPauseCustom(true); }} style={chip(pauseMode === "date" && pauseCustom)}>Pick a date</button>
                <button type="button" onClick={() => { setPauseMode("forever"); setPauseCustom(false); }} style={chip(pauseMode === "forever")}>Until I resume</button>
              </div>

              {pauseMode === "date" && pauseCustom && (
                <div style={{ display: "flex", justifyContent: "center", marginTop: 14 }}>
                  <NativePickerInput
                    type="date"
                    value={pauseUntilDate}
                    min={t}
                    onChange={e => setPauseUntilDate(e.target.value)}
                    style={{ width: 180, padding: "6px 2px", border: "none", borderBottom: "2px solid var(--c-primary)", borderRadius: 0, fontSize: 15, fontFamily: "inherit", color: "var(--c-text-mid)", background: "transparent", textAlign: "center", boxSizing: "border-box", outline: "none" }}
                  />
                </div>
              )}

              <div style={{ minHeight: 20, marginTop: 14, textAlign: "center", fontSize: 13, fontWeight: 700, color: "var(--c-text-mid)" }}>{summary}</div>

              <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
                <button onClick={confirmPause} disabled={!canPause}
                  style={{ flex: 1, padding: "13px 0", borderRadius: 999, border: "none", background: "linear-gradient(135deg, var(--c-primary-light), var(--c-primary-deep))", color: "var(--c-on-primary)", fontWeight: 700, fontSize: 15, cursor: canPause ? "pointer" : "default", opacity: canPause ? 1 : 0.5, fontFamily: "inherit" }}>
                  Pause Habit
                </button>
                <button onClick={closePause}
                  style={{ padding: "13px 22px", borderRadius: 999, border: "1.5px solid var(--c-border)", background: "var(--c-bg-card)", color: "var(--c-text-mid)", fontWeight: 600, fontSize: 15, cursor: "pointer", fontFamily: "inherit" }}>
                  Cancel
                </button>
              </div>
            </div>
          </div>,
          document.body
        );
      })()}

      {manageModal && (() => {
        const DAYS_M = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
        const getFreqLabel = (h) =>
          h.frequencyType === "daily" ? "Daily"
          : h.frequencyType === "interval" ? intervalLabel(h)
          : h.frequencyType === "count" ? `${h.weeklyCount || 1}×/week`
          : h.frequencyType === "specific" ? ((h.days || []).map(d => DAYS_M[d]).join(" ") || "Specific days")
          : h.frequencyType === "monthly" ? (h.monthlyMode === "count" ? `${h.monthlyCount || 10}×/month` : "Monthly (dates)")
          : h.frequencyType || "Daily";


        return createPortal(
          <div
            onClick={manageSheet.dismiss}
            className="no-nav-blur"
            style={{
              position: "fixed", inset: 0, zIndex: 9100,
              display: "flex", alignItems: "flex-end", justifyContent: "center",
              fontFamily: "'Quicksand', 'Nunito', sans-serif",
            }}
          >
            {/* The dim is its own layer so a swipe down can fade it without fading the sheet. */}
            <div aria-hidden ref={manageSheet.dimRef} style={{ position: "absolute", inset: 0, background: "rgba(40,20,40,0.6)" }} />
            <div
              ref={manageSheet.sheetRef}
              onClick={e => e.stopPropagation()}
              style={{
                position: "relative", width: "100%", maxWidth: 540,
                background: "var(--c-bg-card)",
                borderRadius: "32px 32px 0 0",
                boxShadow: "0 -8px 40px rgba(180,80,120,0.22)",
                maxHeight: "82vh",
                display: "flex", flexDirection: "column",
                animation: "sheetUp 0.32s cubic-bezier(0.22,1,0.36,1) both",
              }}
            >
              <style>{`@keyframes sheetUp { from { transform: translateY(100%); opacity: 0; } to { transform: translateY(0); opacity: 1; } }`}</style>

              {/* Drag pill */}
              <div style={{ display: "flex", justifyContent: "center", padding: "12px 0 0" }}>
                <div style={{ width: 36, height: 4, borderRadius: 2, background: "#d4789a55" }} />
              </div>

              {/* Header */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 20px 10px" }}>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: "var(--c-text)" }}>⚙ Manage Habits</div>
                  <div style={{ fontSize: 11, color: "var(--c-text-soft)", marginTop: 2 }}>
                    {habits.length} habit{habits.length !== 1 ? "s" : ""} total{habits.filter(h => isHabitPaused(h)).length > 0 ? ` · ${habits.filter(h => isHabitPaused(h)).length} paused` : ""} · tap to edit or delete
                  </div>
                </div>
                <button
                  onClick={() => setManageModal(false)}
                  style={{ background: "transparent", border: "none", fontSize: 20, cursor: "pointer", color: "var(--c-text-soft)", lineHeight: 1, padding: 4, minHeight: "auto", minWidth: "auto" }}
                ><X size={16} /></button>
              </div>

              {/* No tab switcher — unified list */}
              <div style={{ height: "1px", background: "var(--c-bg-active)", margin: "0 20px" }} />

              {/* Habit list */}
              <div style={{ overflowY: "auto", flex: 1, padding: "4px 0 32px" }}>
                {habits.length === 0 ? (
                  <div style={{ textAlign: "center", padding: "40px 20px" }}>
                    <div style={{ fontSize: 32, marginBottom: 10 }}>✨</div>
                    <p style={{ fontSize: 14, color: isDarkBgTheme() ? "#ffffff" : "#000000", margin: 0, fontWeight: 800 }}>No habits yet</p>
                    <p style={{ fontSize: 12, color: isDarkBgTheme() ? "rgba(255,255,255,0.75)" : "rgba(0,0,0,0.6)", margin: "6px 0 0" }}>
                      Close this and tap <strong>+ New habit</strong> to add one.
                    </p>
                  </div>
                ) : (() => {
                  // ── Group habits by category, then split To Do vs Restricted within each ──
                  const catOrder = categories.map(c => c.id);
                  const grouped = {};
                  habits.forEach(h => {
                    const cid = h.goalId || "__none__";
                    if (!grouped[cid]) grouped[cid] = { todo: [], restricted: [] };
                    const isRestricted = h.habitType === "limited" || h.habitType === "nottodo";
                    if (isRestricted) grouped[cid].restricted.push(h);
                    else grouped[cid].todo.push(h);
                  });
                  const sortedCatIds = [
                    ...catOrder.filter(id => grouped[id]),
                    ...Object.keys(grouped).filter(id => id !== "__none__" && !catOrder.includes(id)),
                    ...(grouped["__none__"] ? ["__none__"] : []),
                  ];

                  const renderHabitRow = (h) => {
                    const hCat = h.goalId ? categories.find(c => c.id === h.goalId) : null;
                    const isOpen = expandedManageHabit === h.id;
                    const freqLabel = getFreqLabel(h);
                    const todayDue = isHabitDueToday(h);
                    const isPaused = isHabitPaused(h);
                    const isRestricted = h.habitType === "limited" || h.habitType === "nottodo";
                    const typeChipColor = isRestricted ? "#c0392b" : "#d4789a";
                    const typeChipBg = isRestricted ? "#fdeaea" : "#fce4ee";
                    const typeBorder = isRestricted ? "#f5c0c0" : "#f0d8ea";
                    const typeLabel = h.habitType === "nottodo" ? "Full Stop" : h.habitType === "limited" ? "Limited" : "To Do";
                    const typeEmoji = h.habitType === "nottodo" ? "🚫" : h.habitType === "limited" ? "⚡" : "🌿";
                    const pauseLabel = h.pausedUntil === "9999-12-31"
                      ? "paused indefinitely"
                      : h.pausedUntil
                        ? `paused until ${new Date(h.pausedUntil + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}`
                        : null;
                    const pauseHistory = h.pauseHistory || (h.pauseReason ? [{ pausedOn: h.createdAt || t, pausedUntil: h.pausedUntil, reason: h.pauseReason }] : []);
                    const latestPause = pauseHistory.length > 0 ? pauseHistory[pauseHistory.length - 1] : null;
                    const currentReason = latestPause?.reason || "";
                    return (
                      <div key={h.id} style={{ borderBottom: `1px solid ${isPaused ? "#c8d4f0" : typeBorder}`, background: isPaused ? "#f5f7ff" : "transparent" }}>
                        {/* Main row */}
                        <div
                          onClick={() => setExpandedManageHabit(isOpen ? null : h.id)}
                          style={{ display: "flex", alignItems: "center", gap: 10, padding: "13px 20px", cursor: "pointer", userSelect: "none", background: isOpen ? (isPaused ? "#e8eeff99" : typeChipBg + "99") : "transparent", transition: "background 0.15s" }}
                        >
                          <div style={{ width: 8, height: 8, borderRadius: "50%", background: isPaused ? "#9aabcc" : (hCat?.color || h.color || typeChipColor), flexShrink: 0 }} />
                          <HabitIcon h={h} size={22} style={{ flexShrink: 0, opacity: isPaused ? 0.5 : 1 }} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 14, fontWeight: 700, color: isPaused ? "#7a8ab0" : "#3a3040", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{h.name}{goalTag(h) ? <span style={{ fontSize: 10, fontWeight: 700, color: vibrantAccent(hCat?.color || h.color || "#d4789a"), marginLeft: 4 }}>({goalTag(h)})</span> : null}</div>
                            <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 3, flexWrap: "wrap" }}>
                              {isPaused ? (
                                <>
                                  <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 99, background: "var(--c-bg-active)", color: "var(--c-text-mid)", display: "flex", alignItems: "center", gap: 3 }}>
                                    <PauseCircle size={9} /> {pauseLabel}
                                  </span>
                                  {currentReason && (
                                    <span style={{ fontSize: 10, color: "#9aabcc", fontStyle: "italic", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "160px" }}>
                                      "{currentReason}"
                                    </span>
                                  )}
                                </>
                              ) : (
                                <>
                                  {isRestricted && <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 99, background: typeChipBg, color: typeChipColor }}>{typeEmoji} {typeLabel}</span>}
                                  {!isRestricted && <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 7px", borderRadius: 99, background: "var(--c-bg-soft)", color: "#9a70c0" }}>{freqLabel}</span>}
                                  {!todayDue && !isPaused && <span style={{ fontSize: 10, color: "var(--c-text-soft)" }}>not assigned today</span>}
                                </>
                              )}
                            </div>
                          </div>
                          <span style={{ fontSize: 14, color: "var(--c-text-soft)", transform: isOpen ? "rotate(90deg)" : "rotate(0deg)", transition: "transform 0.2s", lineHeight: 1, flexShrink: 0 }}>›</span>
                        </div>

                        {/* Expanded panel */}
                        {isOpen && (
                          <div style={{ display: "flex", flexDirection: "column", gap: 0, padding: "2px 20px 8px", background: isPaused ? "#eef1fc55" : (typeChipBg + "55") }}>
                            {isPaused && currentReason && (
                              <div style={{ fontSize: 12, color: "#7a8ab0", fontStyle: "italic", padding: "8px 10px", background: "var(--c-bg-soft)", borderRadius: 10, marginBottom: 8, lineHeight: 1.5, border: "1px solid #dde4f8" }}>
                                💬 "{currentReason}"
                              </div>
                            )}
                            {isPaused && pauseHistory.length > 1 && (
                              <div style={{ marginBottom: 8 }}>
                                <p style={{ fontSize: 10, fontWeight: 700, color: "#9aabcc", textTransform: "uppercase", letterSpacing: "0.06em", margin: "0 0 5px" }}>Previous pauses</p>
                                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                                  {[...pauseHistory].reverse().slice(1).map((entry, i) => {
                                    const friendlyOn = new Date(entry.pausedOn + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
                                    const friendlyUntil = entry.pausedUntil === "9999-12-31" ? "indefinitely" : entry.pausedUntil ? new Date(entry.pausedUntil + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "-";
                                    return (
                                      <div key={i} style={{ background: "var(--c-bg-soft)", borderRadius: 8, padding: "6px 10px", border: "1px solid #e8eeff" }}>
                                        <div style={{ fontSize: 10, color: "#9aabcc", fontWeight: 600, marginBottom: 2 }}>{friendlyOn} · until {friendlyUntil}</div>
                                        {entry.reason
                                          ? <div style={{ fontSize: 11, color: "#7a8ab0", fontStyle: "italic" }}>"{entry.reason}"</div>
                                          : <div style={{ fontSize: 11, color: "#b8c8e0" }}>no reason noted</div>}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                            <div style={{ display: "flex", gap: 6 }}>
                              {isPaused ? (
                                <>
                                  <button onClick={() => { resumeHabit(h.id); setExpandedManageHabit(null); }} style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 5, fontSize: 12, fontWeight: 700, color: "var(--c-text-mid)", background: "var(--c-bg-active)", border: "none", boxShadow: "0 1px 5px rgba(120,60,100,0.12)", borderRadius: 10, padding: "6px 6px", cursor: "pointer" }}>
                                    <Play size={16} fill="currentColor" fillOpacity={0.22} strokeWidth={2.2} /> Resume
                                  </button>
                                  <button onClick={() => { openEdit(h); setExpandedManageHabit(null); setManageModal(false); }} style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 5, fontSize: 12, fontWeight: 600, color: "var(--c-text-mid)", background: "var(--c-bg-soft)", border: "none", boxShadow: "0 1px 5px rgba(120,60,100,0.12)", borderRadius: 10, padding: "6px 11px", cursor: "pointer" }}>
                                    <Pencil size={16} fill="currentColor" fillOpacity={0.22} strokeWidth={2.2} />
                                  </button>
                                  <button onClick={() => { setConfirmDeleteHabit(h); setExpandedManageHabit(null); }} style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 4, fontSize: 13, fontWeight: 700, color: "#e8647a", background: "var(--c-bg-soft)", border: "none", boxShadow: "0 1px 5px rgba(120,60,100,0.12)", borderRadius: 10, padding: "6px 11px", cursor: "pointer", flexShrink: 0 }}>
                                    <Trash2 size={16} fill="currentColor" fillOpacity={0.22} strokeWidth={2.2} />
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button onClick={() => { openEdit(h); setExpandedManageHabit(null); setManageModal(false); }} style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 5, fontSize: 12, fontWeight: 700, color: "var(--c-text-mid)", background: "var(--c-bg-soft)", border: "none", boxShadow: "0 1px 5px rgba(120,60,100,0.12)", borderRadius: 10, padding: "6px 6px", cursor: "pointer" }}>
                                    <Pencil size={16} fill="currentColor" fillOpacity={0.22} strokeWidth={2.2} />Edit
                                  </button>
                                  <button onClick={() => { openPauseModal(h); setExpandedManageHabit(null); setManageModal(false); }} style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 5, fontSize: 12, fontWeight: 600, color: "var(--c-text-mid)", background: "var(--c-bg-soft)", border: "none", boxShadow: "0 1px 5px rgba(120,60,100,0.12)", borderRadius: 10, padding: "6px 11px", cursor: "pointer" }}>
                                    <Pause size={16} fill="currentColor" fillOpacity={0.22} strokeWidth={2.2} />
                                  </button>
                                  <button onClick={() => { setConfirmDeleteHabit(h); setExpandedManageHabit(null); }} style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 4, fontSize: 13, fontWeight: 700, color: "#e8647a", background: "var(--c-bg-soft)", border: "none", boxShadow: "0 1px 5px rgba(120,60,100,0.12)", borderRadius: 10, padding: "6px 11px", cursor: "pointer", flexShrink: 0 }}>
                                    <Trash2 size={16} fill="currentColor" fillOpacity={0.22} strokeWidth={2.2} />
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  };

                  return sortedCatIds.map(cid => {
                    const cat = cid !== "__none__" ? categories.find(c => c.id === cid) : null;
                    const { todo = [], restricted = [] } = grouped[cid] || {};
                    if (todo.length === 0 && restricted.length === 0) return null;
                    const totalInCat = todo.length + restricted.length;
                    return (
                      <div key={cid}>
                        {/* ── Category header ── */}
                        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 20px 5px", background: cat ? (cat.color + "12") : "#f8f0f855", borderBottom: `1.5px solid ${cat ? (cat.color + "30") : "#e8d8ea"}` }}>
                          {cat ? (
                            <>
                              <div style={{ width: 3, height: 14, borderRadius: 2, background: cat.color, flexShrink: 0 }} />
                              <CategoryIcon cat={cat} size={12} />
                              <span style={{ fontSize: 10, fontWeight: 800, color: readableOnWhite(cat.color), textTransform: "uppercase", letterSpacing: "0.07em", flex: 1 }}>{cat.title}</span>
                              <span style={{ fontSize: 10, fontWeight: 600, color: readableOnWhite(cat.color), background: cat.color + "18", borderRadius: 99, padding: "1px 6px" }}>{totalInCat}</span>
                            </>
                          ) : (
                            <>
                              <div style={{ width: 3, height: 14, borderRadius: 2, background: "#c0a0b8", flexShrink: 0 }} />
                              <span style={{ fontSize: 10, fontWeight: 800, color: "var(--c-text-soft)", textTransform: "uppercase", letterSpacing: "0.07em", flex: 1 }}>Uncategorized</span>
                              <span style={{ fontSize: 10, fontWeight: 600, color: "var(--c-text-soft)", background: "var(--c-bg-soft)", borderRadius: 99, padding: "1px 6px" }}>{totalInCat}</span>
                            </>
                          )}
                        </div>

                        {/* ── To Do sub-group ── */}
                        {todo.length > 0 && (
                          <>
                            <div style={{ display: "flex", alignItems: "center", gap: 5, padding: "5px 20px 2px", background: "#ffffff22" }}>
                              <span style={{ fontSize: 9, fontWeight: 800, color: "var(--c-text-accent)", textTransform: "uppercase", letterSpacing: "0.07em" }}>To Do</span>
                              <span style={{ fontSize: 9, color: "#d4789a66" }}>· {todo.length}</span>
                            </div>
                            {todo.map(h => renderHabitRow(h))}
                          </>
                        )}

                        {/* ── Restricted sub-group ── */}
                        {restricted.length > 0 && (
                          <>
                            <div style={{ display: "flex", alignItems: "center", gap: 5, padding: "5px 20px 2px", background: "#fdeaea22", marginTop: todo.length > 0 ? 2 : 0 }}>
                              <span style={{ fontSize: 9, fontWeight: 800, color: "#c0392b", textTransform: "uppercase", letterSpacing: "0.07em" }}>🚫 Restricted</span>
                              <span style={{ fontSize: 9, color: "#c0392b66" }}>· {restricted.length}</span>
                            </div>
                            {restricted.map(h => renderHabitRow(h))}
                          </>
                        )}
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          </div>,
          (document.getElementById("portal-root") || document.body)
        );
      })()}

      {/* ── Floating Action Button — Add Habit (portalled to body so it's truly fixed) ── */}
      {!isDesktop && !showForm && !editingHabit && !manageModal && !settingsOpen && !pauseModal && !amountSheet && createPortal(
        <button
          data-tour="fab-add"
          onClick={() => { setForm({ ...emptyForm, habitType: "todo" }); setShowForm(true); }}
          title="New habit"
          className="habit-fab-mobile"
          style={{
            position: "fixed",
            bottom: "calc(90px + var(--sab, 0px))",
            right: 24,
            zIndex: 99999,
            width: 56,
            height: 56,
            borderRadius: 18,
            border: "none",
            background: "linear-gradient(135deg, var(--c-primary-light) 0%, var(--c-primary-deep) 100%)",
            color: "var(--c-on-primary)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 4px 18px rgba(180,80,160,0.45)",
            cursor: "pointer",
            transition: "transform 0.15s, box-shadow 0.15s",
          }}
          onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.08)"; e.currentTarget.style.boxShadow = "0 6px 24px rgba(180,80,160,0.55)"; }}
          onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; e.currentTarget.style.boxShadow = "0 4px 18px rgba(180,80,160,0.45)"; }}
        >
          <Plus size={26} strokeWidth={2.5} />
        </button>,
        document.body
      )}

      {/* ── Desktop unified add-FAB — new habit OR new category. Anchored to the
          bottom-left, aligned with the main sidebar nav column (fixed, left: 12,
          width: 60) so it sits directly under the tab rail. Menu opens upward
          since the button sits near the bottom. ── */}
      {isDesktop && !showForm && !editingHabit && !manageModal && !settingsOpen && !pauseModal && !amountSheet && createPortal(
        <div style={{ position: "fixed", bottom: "calc(30px + var(--sab, 0px))", left: 12, width: 60, display: "flex", justifyContent: "center", zIndex: 99999 }}>
          {addMenuOpen && (
            <>
              <div onClick={() => setAddMenuOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 0 }} />
              <div style={{
                position: "absolute", bottom: "calc(100% + 10px)", left: 0, zIndex: 1,
                background: "var(--c-bg-card)", borderRadius: 14, padding: 6, minWidth: 184,
                border: "1.5px solid var(--c-border-mid)",
                boxShadow: "0 8px 28px rgba(180,80,140,0.22)",
                animation: "viewPanelIn 0.18s cubic-bezier(0.34,1.56,0.64,1) both",
              }}>
                <button
                  onClick={() => { setAddMenuOpen(false); setForm({ ...emptyForm, habitType: "todo" }); setShowForm(true); }}
                  style={{ display: "flex", alignItems: "center", gap: 9, width: "100%", padding: "10px 12px", borderRadius: 10, border: "none", background: "transparent", color: "var(--c-text)", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", textAlign: "left" }}
                  onMouseEnter={e => e.currentTarget.style.background = "var(--c-bg-soft)"}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                >
                  <Sprout size={15} style={{ color: "var(--c-text-accent)" }} /> New habit
                </button>
                <button
                  onClick={() => { setAddMenuOpen(false); openAddCategoryRef.current?.(); catColRef.current?.scrollTo({ top: 0, behavior: "smooth" }); }}
                  style={{ display: "flex", alignItems: "center", gap: 9, width: "100%", padding: "10px 12px", borderRadius: 10, border: "none", background: "transparent", color: "var(--c-text)", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", textAlign: "left" }}
                  onMouseEnter={e => e.currentTarget.style.background = "var(--c-bg-soft)"}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                >
                  <Layers size={15} style={{ color: "var(--c-text-accent)" }} /> New category
                </button>
              </div>
            </>
          )}
          <button
            data-tour="fab-add"
            onClick={() => setAddMenuOpen(o => !o)}
            title="Add"
            style={{
              position: "relative", zIndex: 1,
              width: 56, height: 56, borderRadius: 18, border: "none",
              background: "linear-gradient(135deg, var(--c-primary-light) 0%, var(--c-primary-deep) 100%)",
              color: "var(--c-on-primary)",
              display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: "0 4px 18px rgba(180,80,160,0.45)",
              cursor: "pointer", fontFamily: "inherit",
              transform: addMenuOpen ? "rotate(45deg)" : "rotate(0deg)",
              transition: "transform 0.2s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.15s",
            }}
            onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 6px 24px rgba(180,80,160,0.55)"; }}
            onMouseLeave={e => { e.currentTarget.style.boxShadow = "0 4px 18px rgba(180,80,160,0.45)"; }}
          >
            <Plus size={26} strokeWidth={2.5} />
          </button>
        </div>,
        document.body
      )}
      </div>

      {/* ── Graphs column — visible beside habits on desktop (no Insights button needed) ── */}
      {isDesktop && (
      <div className="habits-graph-col" style={{ flex: "1 1 440px", minWidth: 360, position: "sticky", top: 0, maxHeight: "calc(100vh - 40px)", overflowY: "auto" }}>
        <ProfileTab section="habits" isDesktop={isDesktop}
          habits={habits} setHabits={setHabits}
          habitLogs={habitLogs} setHabitLogs={setHabitLogs}
          habitSkips={habitSkips} setHabitSkips={setHabitSkips}
          tasks={tasks} setTasks={setTasks} categories={categories} bgTheme={bgTheme} />
      </div>
      )}
    </div>
  );
}
